
import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from 'react';
import { LoadSession, CurrentSessionHasBallSeries, GetCurrentSession, ReplaceShot, GetSessionHeader, GetSetupHeader, SetCurrentSession, ResetSessions, EmulateShot, EmulateRandomShot, UpdateSetup, EmulateCORShot, EmulateRandomCORShot, UpdateShotCheck, SelectSessionSetup, IsPlateCorSetup, GetPlateSetupForCorSetup, getDefaultCorPlateMass } from '../isdlData/sessionData';
import { Drawer, Button, Box, Modal, Switch } from '@mui/material';
import Setups from './Setups';
import SetupsCombo from './SetupsCombo';
import Settings from './Settings';
import Options from './Options';
import ShotListsPanel from './ShotListsPanel';
import DataGrid from 'react-data-grid';
import ShotListHeader from './custom/ShotListHeader';
import Widgets from './Widgets';
import MiniWidgets from './MiniWidgets';
import ThreeDTraj from './ThreeDTraj';
import TopDown from './TopDown';
import './Components.css';
import { session_update, process_toptracer_shots } from '../mobile/mobileHandler';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { LMList, SetupColors, MasterCapabilitiesList } from '../utils/LMConstants';
import { GearIcon,FolderIcon, Cross2Icon, WidgetButtonFlowerIcon,DocumentSummaryIcon, SettingsIcon, WidgetButtonIcon, ShotListButtonIcon, ThreeDTrajIcon, TopDownIcon, SetupsFlyoutButtonIcon, FlyoutButtonIcon, OptionsIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ChevronUpIcon, FlagIcon } from '../icons/Icons';
import { detectDeviceType, detectOrientation, getAllMeasurementsForLMInfo, getBuildType, formatShotData, getImpactMeasurementKeys, getImpactTargetHalfMm, isValidRobotTestDataField } from '../utils/Utilities';
import GolferDisplay from './GolferDisplay';
import SetupsList from './SetupsList';
import LMDisplaySelector from './LMDisplaySelector';
import ResearchQuadMasses, { buildResearchQuadCorPayload } from './ResearchQuadMasses';
import ResetSession from './ResetSession';
import ProcessTTShotsModal from './ProcessTTShotsModal';
import { Save } from './export/file';
import LoftAttack from './LoftAttack';
import FacePath from './FacePath';
import ImpactLocation from './ImpactLocation';
import ImpactTargetBox from './ImpactTargetBox';
import PostShotModal from './PostShotModal';
import { AppContext, LMDrawingContext, LMShowEllipseContext, ViewOnlyContext, ThemeContext, TaskIdDataContext } from '../contexts/AppContext';
import { useAppStateService } from '../contexts/AppState';
import { getTaskInfo } from '../isdlData/taskmanData';
import { getShowDetailedSummaryPreference, setShowDetailedSummaryPreference, USER_PREFERENCES_IMPORTED_EVENT } from '../utils/userPreferences';



function Session({onLoadSession, onLoadModal, index}){
    const [selectedSession, setSelectedSession] = useState(null);
    const getEmptyResearchQuadMassesForSession = useCallback((session) => ({
        plate: getDefaultCorPlateMass(session?.UserName, session?.UserId),
        plateBall: '45.6',
        head: '',
        headBall: '45.6',
        faceDeformationDelta: '',
        faceDeformationStart: '',
        faceDefomationEnd: ''
    }), []);
    const emptyResearchQuadMasses = useMemo(() => (
        getEmptyResearchQuadMassesForSession(selectedSession)
    ), [getEmptyResearchQuadMassesForSession, selectedSession]);
    const normalizeResearchQuadMassInput = useCallback((value) => (value === null || value === undefined ? '' : String(value)), []);
    const hasStoredCannonCorInputs = useCallback((setup) => {
        const inputs = setup?.CannonCor?.inputs;
        if (!inputs) {
            return false;
        }

        return ['plateMass', 'plateBallMass', 'headMass', 'headBallMass']
            .some((key) => inputs[key] !== undefined && inputs[key] !== null && `${inputs[key]}` !== '');
    }, []);
    const getCorFamilySetups = useCallback((session, setup) => {
        if (!Array.isArray(session?.SessionSetups) || !setup?.TestParameters?.CORType) {
            return setup ? [setup] : [];
        }

        const setupLetter = setup.LetterId || '';
        const setupHead = setup.HeadSN || setup.BaseComponentSN || setup.ClubId || '';

        return session.SessionSetups.filter((candidate) => {
            if (!candidate?.TestParameters?.CORType) {
                return false;
            }

            const candidateLetter = candidate.LetterId || '';
            if (setupLetter || candidateLetter) {
                if (candidateLetter !== setupLetter) {
                    return false;
                }
            }

            const candidateHead = candidate.HeadSN || candidate.BaseComponentSN || candidate.ClubId || '';
            if (setupHead && candidateHead) {
                return candidateHead === setupHead;
            }

            return true;
        });
    }, []);
    const getMassesFromSetup = useCallback((setup, sessionOverride = null) => {
        const resolvedSession = sessionOverride ?? selectedSession;
        const familySetups = getCorFamilySetups(resolvedSession, setup);
        const sourceSetup =
            familySetups.find((candidate) => candidate?.Id === setup?.Id && hasStoredCannonCorInputs(candidate)) ||
            familySetups.find((candidate) => IsPlateCorSetup(candidate) && hasStoredCannonCorInputs(candidate)) ||
            familySetups.find((candidate) => hasStoredCannonCorInputs(candidate)) ||
            setup;
        const inputs = sourceSetup?.CannonCor?.inputs;
        const results = setup?.CannonCor?.results || {};
        const fallbackMasses = getEmptyResearchQuadMassesForSession(resolvedSession);

        return {
            plate: normalizeResearchQuadMassInput(inputs?.plateMass ?? fallbackMasses.plate),
            plateBall: normalizeResearchQuadMassInput(inputs?.plateBallMass ?? fallbackMasses.plateBall),
            head: normalizeResearchQuadMassInput(inputs?.headMass ?? fallbackMasses.head),
            headBall: normalizeResearchQuadMassInput(inputs?.headBallMass ?? fallbackMasses.headBall),
            faceDeformationDelta: normalizeResearchQuadMassInput(results?.faceDeformationDelta ?? fallbackMasses.faceDeformationDelta),
            faceDeformationStart: normalizeResearchQuadMassInput(results?.faceDeformationStart ?? fallbackMasses.faceDeformationStart),
            faceDeformationEnd: normalizeResearchQuadMassInput(results?.faceDeformationEnd ?? fallbackMasses.faceDeformationEnd)
        };
    }, [getCorFamilySetups, getEmptyResearchQuadMassesForSession, hasStoredCannonCorInputs, normalizeResearchQuadMassInput, selectedSession]);

    const { taskIdData, setTaskIdData } = useContext(TaskIdDataContext);
    const [showDetailedSummary, setShowDetailedSummary] = useState(() => getShowDetailedSummaryPreference());
    const [selectedSetup, setSelectedSetup] = useState(null);
    const [selectedShot, setSelectedShot] = useState(null);
    const [selectedShotSetupId, setSelectedShotSetupId] = useState(null);
    const [shotsChangedVersion, setShotsChangedVersion] = useState(0);
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
   // const [showDetailedSummary, setShowDetailedSummary]= useState(false);
    const [setupSelectionOpen, setSetupSelectionOpen] = useState(false);
    const [postBallSeriesShotModalOpen, setPostBallSeriesShotModalOpen] = useState(false);
    const [postBallSeriesShotEndedModalOpen, setPostBallSeriesShotEndedModalOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [appWorking, setAppWorking] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [currentView, setCurrentView] = useState("SHOTLIST");
    const [UseSeries, SetUseSeries] = useState(false);
    const [isMobile, setIsMobile] = useState(detectDeviceType() === "Mobile");
    const [orientation, setOrientation] = useState(detectOrientation());
    const [replacingShot, setReplacingShot] = useState(null);
    const [researchQuadMasses, setResearchQuadMasses] = useState(emptyResearchQuadMasses);
    const [cannonCorConfirmation, setCannonCorConfirmation] = useState({
        open: false,
        editable: false,
        setupId: null,
        error: ''
    });
    const [shotPreprocessing, setShotPreprocessing] = useState(false);
    const setupsShotsChangedRef = useRef(null);
    const registerSetupsShotsChanged = useCallback((callback) => {
        setupsShotsChangedRef.current = callback;
    }, []);
    const applyShotSelection = useCallback((shot, setupId = null) => {
        if (shot) {
            setSelectedShot(shot);
            setSelectedShotSetupId(setupId);
        } else {
            setSelectedShot(null);
            setSelectedShotSetupId(null);
        }
    }, []);
    const refreshSetupsFromShots = useCallback(() => {
        if (typeof setupsShotsChangedRef.current === 'function') {
            setupsShotsChangedRef.current();
        }
        setShotsChangedVersion((prev) => prev + 1);
        const currentSession = GetCurrentSession();

        if (currentSession) {
            const fallbackSelectedSetupId = Array.isArray(currentSession.SessionSetups)
                ? currentSession.SessionSetups.find((setup) => setup?.IsSelected)?.Id || currentSession.SessionSetups[0]?.Id || null
                : null;
            const activeSetupId = selectedSetup?.Id || selectedShotSetupId || fallbackSelectedSetupId;
            const currentSetup = activeSetupId && Array.isArray(currentSession.SessionSetups)
                ? currentSession.SessionSetups.find((setup) => setup?.Id === activeSetupId) || null
                : null;

            let nextSession = {
                ...currentSession,
                SessionSetups: Array.isArray(currentSession.SessionSetups)
                    ? currentSession.SessionSetups.map((setup) => ({ ...setup }))
                    : currentSession.SessionSetups
            };
            let nextSetup = currentSetup && Array.isArray(nextSession.SessionSetups)
                ? nextSession.SessionSetups.find((setup) => setup?.Id === currentSetup.Id) || null
                : null;

            const plateSetup = Array.isArray(nextSession.SessionSetups)
                ? nextSession.SessionSetups.find((setup) => IsPlateCorSetup(setup)) || null
                : null;
            let confirmationChanged = false;

            if (Array.isArray(nextSession.SessionSetups)) {
                nextSession.SessionSetups = nextSession.SessionSetups.map((setup) => {
                    if (!setup?.CannonCor?.confirmation || IsPlateCorSetup(setup)) {
                        return setup;
                    }

                    const nextPayload = buildResearchQuadCorPayload({
                        masses: getMassesFromSetup(setup),
                        selectedSetup: setup,
                        plateSetup
                    });
                    const nextSignature = nextPayload.meta?.confirmationSignature || '';
                    const existingConfirmation = setup.CannonCor.confirmation;

                    if (
                        existingConfirmation.confirmed === true &&
                        existingConfirmation.signature !== nextSignature
                    ) {
                        confirmationChanged = true;
                        const nextCannonCor = {
                            ...(setup.CannonCor || {}),
                            confirmation: {
                                confirmed: false,
                                signature: nextSignature
                            }
                        };

                        const originalSetup = currentSession.SessionSetups.find((candidate) => candidate?.Id === setup.Id);
                        if (originalSetup) {
                            UpdateSetup(originalSetup, {
                                CannonCor: nextCannonCor
                            });
                        }

                        return {
                            ...setup,
                            CannonCor: nextCannonCor
                        };
                    }

                    return setup;
                });
            }

            if (confirmationChanged) {
                SetCurrentSession(nextSession);
                nextSetup = currentSetup && Array.isArray(nextSession.SessionSetups)
                    ? nextSession.SessionSetups.find((setup) => setup?.Id === currentSetup.Id) || null
                    : nextSetup;
            }

            setSelectedSession(nextSession);
            setSelectedSetup(nextSetup);

            if (selectedShot && selectedShotSetupId && nextSetup?.Shots) {
                const nextShot = nextSetup.Shots.find((shot) => shot?.ShotNumber === selectedShot.ShotNumber) || null;
                applyShotSelection(nextShot, nextShot ? selectedShotSetupId : null);
            } else if (selectedShot && selectedSetup?.Id === activeSetupId && nextSetup?.Shots) {
                const nextShot = nextSetup.Shots.find((shot) => shot?.ShotNumber === selectedShot.ShotNumber) || null;
                applyShotSelection(nextShot, nextShot ? activeSetupId : null);
            }
        }

        if (typeof setupsShotsChangedRef.current === 'function') {
            setupsShotsChangedRef.current();
        }
        setShotsChangedVersion((prev) => prev + 1);
    }, [applyShotSelection, selectedSetup?.Id, selectedShot, selectedShotSetupId]);
    const handleDetailedSummaryChange = useCallback((checked) => {
        setShowDetailedSummary(checked);
        setShowDetailedSummaryPreference(checked);
    }, []);
    const normalizeSessionSelection = useCallback((session, activeSetupId) => {
        if (!session || !Array.isArray(session.SessionSetups) || !activeSetupId) {
            return session;
        }

        let hasChanges = false;
        const updatedSetups = session.SessionSetups.map((setup) => {
            if(!setup){
                return setup;
            }

            const isActive = setup.Id === activeSetupId;
            const nextIsSelected = isActive;
            const nextIsChecked = isActive;
            const previousChecked = !!setup.IsChecked;

            if (setup.IsSelected === nextIsSelected && previousChecked === nextIsChecked) {
                return setup;
            }

            hasChanges = true;
            return {
                ...setup,
                IsSelected: nextIsSelected,
                IsChecked: nextIsChecked
            };
        });

        if (!hasChanges) {
            return session;
        }

        return {
            ...session,
            SessionSetups: updatedSetups
        };
    }, []);
    useEffect(() => {
        const handleImportedPreferences = () => {
            setShowDetailedSummary(getShowDetailedSummaryPreference());
            const leftStateLoaded = localStorage.getItem('desktop_leftPanelState');
            const leftState = leftStateLoaded ? (leftStateLoaded === 'true') : true;
            setLeftPanelState(leftState);

            const leftTabLoaded = localStorage.getItem('desktop_leftTabSelection');
            setLeftTabSelection(leftTabLoaded || 'TRAJ');

            const rightStateLoaded = localStorage.getItem('desktop_rightPanelState');
            const rightState = rightStateLoaded ? (rightStateLoaded === 'true') : true;
            setRightPanelState(rightState);
        };

        window.addEventListener(USER_PREFERENCES_IMPORTED_EVENT, handleImportedPreferences);
        return () => {
            window.removeEventListener(USER_PREFERENCES_IMPORTED_EVENT, handleImportedPreferences);
        };
    }, []);
    const handleShotSelected = useCallback((shot, setupId = null) => {
        applyShotSelection(shot, setupId);
    }, [applyShotSelection]);
    const handleFavoritesToggle = useCallback((value = false) => {
        setShowFavoritesOnly(value);
    }, []);
    const applySetupUpdateLocally = useCallback((setupId, keyvalues) => {
        if (!setupId || !keyvalues) {
            return;
        }

        setSelectedSession((prevSession) => {
            if (!prevSession?.SessionSetups) {
                return prevSession;
            }

            let changed = false;
            const nextSetups = prevSession.SessionSetups.map((setup) => {
                if (!setup || setup.Id !== setupId) {
                    return setup;
                }

                changed = true;
                return {
                    ...setup,
                    ...keyvalues
                };
            });

            return changed
                ? {
                    ...prevSession,
                    SessionSetups: nextSetups
                }
                : prevSession;
        });

        setSelectedSetup((prevSetup) => {
            if (!prevSetup || prevSetup.Id !== setupId) {
                return prevSetup;
            }

            return {
                ...prevSetup,
                ...keyvalues
            };
        });
    }, []);

    const computeSetupSummary = useCallback((setup) => {
        if (!setup || !Array.isArray(setup.Shots) || setup.Shots.length === 0) {
            return { columns: [], rows: [] };
        }

        const summaryData = {};

        setup.Shots.forEach((shot) => {
            if (!shot?.IsChecked || !Array.isArray(shot.LMInfos)) {
                return;
            }

            shot.LMInfos.forEach((lmInfo) => {
                const lmType = lmInfo?.LMType;
                if (!lmType) {
                    return;
                }

                const measurements = getAllMeasurementsForLMInfo(lmInfo);

                Object.entries(measurements).forEach(([key, value]) => {
                    if (value === undefined || value === null || value === '' || value === 'NaN') {
                        return;
                    }

                    const numericValue = Number(value);
                    if (!Number.isFinite(numericValue)) {
                        return;
                    }

                    const columnKey = `${lmType}$${key}`;
                    if (!summaryData[columnKey]) {
                        summaryData[columnKey] = {
                            key,
                            lmType,
                            values: []
                        };
                    }

                    summaryData[columnKey].values.push(numericValue);
                });
            });
        });

        const entries = Object.entries(summaryData).map(([columnKey, data]) => {
            const capability = MasterCapabilitiesList.find(cap => cap.key === data.key);
            const displayName = capability?.display || data.key;
            const unit = capability?.unit || '';
            const label = unit ? `${displayName} (${unit})` : displayName;
            const colorToken = LMList.find(item => item.type === data.lmType)?.color || '#ffffff';

            return {
                columnKey,
                name: displayName,
                unit,
                label,
                color: colorToken,
                capabilityKey: data.key,
                values: data.values
            };
        }).sort((a, b) => a.label.localeCompare(b.label));

        if (!entries.length) {
            return { columns: [], rows: [] };
        }

        const formatSummaryValue = (capabilityKey, value) => {
            const formatted = formatShotData(capabilityKey, value, true);
            return formatted !== null && formatted !== undefined ? formatted : '--';
        };

        const buildRow = (label, computeFn) => {
            const row = { id: `summary-${label.toLowerCase()}`, metric: label };

            entries.forEach((column) => {
                const computedValue = computeFn(column.values);
                row[column.columnKey] = computedValue === null
                    ? '--'
                    : formatSummaryValue(column.capabilityKey, computedValue);
            });

            return row;
        };

        const avgRow = buildRow('Avg', (values) => {
            if (!values || values.length === 0) {
                return null;
            }

            const total = values.reduce((acc, val) => acc + val, 0);
            return total / values.length;
        });

        const stdRow = buildRow('Std', (values) => {
            if (!values || values.length < 2) {
                return null;
            }

            const mean = values.reduce((acc, val) => acc + val, 0) / values.length;
            const variance = values.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / (values.length - 1);
            return Math.sqrt(variance);
        });

        return {
            columns: entries,
            rows: [avgRow, stdRow]
        };
    }, []);


    const setupSummaries = useMemo(() => {
        const sessionSetups = selectedSession?.SessionSetups || [];
        const validSetups = sessionSetups.filter(setup => setup);// && setup.IsChecked);


        if (!validSetups.length) {
            return [];
        }

        const activeSetupId = selectedSetup?.Id || null;
        const orderedSetups = [...validSetups];

        if (activeSetupId) {
            const activeIndex = orderedSetups.findIndex(setup => setup.Id === activeSetupId);
            if (activeIndex > 0) {
                const [activeSetup] = orderedSetups.splice(activeIndex, 1);
                orderedSetups.unshift(activeSetup);
            }
        }

        return orderedSetups.map(setup => {
            const summary = computeSetupSummary(setup);
            const totalShots = Array.isArray(setup.Shots) ? setup.Shots.length : 0;
            const checkedShots = Array.isArray(setup.Shots)
                ? setup.Shots.filter(shot => shot?.IsChecked).length
                : 0;
            const originalIndex = sessionSetups.findIndex(original => original && original.Id === setup.Id);

            return {
                setup,
                summary,
                totalShots,
                checkedShots,
                originalIndex
            };
        });
    }, [selectedSession, selectedSetup, computeSetupSummary]);

    const hasAnySetupSummaryData = useMemo(
        () => setupSummaries.some(entry => entry.summary.columns.length > 0),
        [setupSummaries]
    );

    const resolvedShotSetup = useMemo(() => {
        if (selectedShot && selectedShotSetupId && selectedSession && selectedSession.SessionSetups) {
            return selectedSession.SessionSetups.find(setup => setup.Id === selectedShotSetupId) || null;
        }
        return selectedSetup;
    }, [selectedShot, selectedShotSetupId, selectedSession, selectedSetup]);

    const replacingShotRef = useRef(replacingShot);

    const [replacingShotNum, setReplacingShotNum] = useState(null);

    const appState = useAppStateService();
    
    const { setLMShowEllipse } = useContext(LMShowEllipseContext);
    const { setLMDrawing } = useContext(LMDrawingContext);
    const { viewOnly } = useContext(ViewOnlyContext);
    const { theme, setAppTheme } = useContext(ThemeContext);
    const [userPoints, setUserPoints] = useState([]);
    const isCannonCorConfirmationPending = cannonCorConfirmation.open && !!cannonCorConfirmation.setupId;
    const sessionUsesCorCannon =
        taskIdData?.cor_use_hot_spot === true ||
        taskIdData?.cor_use_center === true;

    const isResearchQuadCorSetup = useCallback((setup) => {
        if (!setup) {
            return false;
        }

        if (setup?.TestParameters?.CORType) {
            return true;
        }

        return Array.isArray(setup.Shots) && setup.Shots.some((shot) =>
            Array.isArray(shot?.LMInfos) &&
            shot.LMInfos.some((lmInfo) => lmInfo?.LMType === 'RESEARCH_QUAD')
        );
    }, []);
    const setupHasCorShotShape = useCallback((setup) => {
        if (!Array.isArray(setup?.Shots)) {
            return false;
        }

        return setup.Shots.some((shot) =>
            Array.isArray(shot?.LMInfos) &&
            shot.LMInfos.some((lmInfo) => {
                if (lmInfo?.LMType !== 'RESEARCH_QUAD') {
                    return false;
                }

                const measurements = {
                    ...(lmInfo.Measurements || {}),
                    ...(lmInfo.CalculatedMeasurements || {})
                };

                return Object.keys(measurements).some((key) => key.includes('post'));
            })
        );
    }, []);

    useEffect(() => {
        const handleAppWorkingState = (event) => {
            setAppWorking(!!event?.detail?.active);
        };

        window.addEventListener('appWorkingState', handleAppWorkingState);

        return () => {
            window.removeEventListener('appWorkingState', handleAppWorkingState);
        };
    }, []);
    const getPlateSetupFromSession = useCallback((session, setup = null) => {
        if (!Array.isArray(session?.SessionSetups)) {
            return null;
        }

        return GetPlateSetupForCorSetup(session, setup) || null;
    }, []);
    const buildCorPayloadForSetup = useCallback((session, setup, massesOverride = null) => {
        if (!setup) {
            return buildResearchQuadCorPayload({
                masses: massesOverride || emptyResearchQuadMasses,
                selectedSetup: null,
                plateSetup: null
            });
        }

        return buildResearchQuadCorPayload({
            masses: massesOverride ?? getMassesFromSetup(setup),
            selectedSetup: setup,
            plateSetup: getPlateSetupFromSession(session, setup)
        });
    }, [emptyResearchQuadMasses, getMassesFromSetup, getPlateSetupFromSession]);

    const persistCannonCorPayload = useCallback((setup, payload) => {
        if (!setup || !payload) {
            return;
        }

        const nextResults = payload.results || {};
        const existingResults = setup.CannonCor?.results || {};
        const existingConfirmation = setup.CannonCor?.confirmation || {};
        const nextSignature = payload.meta?.confirmationSignature || '';
        const nextConfirmation = (
            existingConfirmation.confirmed === true &&
            existingConfirmation.signature === nextSignature
        ) ? existingConfirmation : {
            confirmed: false,
            signature: nextSignature
        };

        const resultsMatch = JSON.stringify(existingResults) === JSON.stringify(nextResults);
        const confirmationMatch = JSON.stringify(existingConfirmation) === JSON.stringify(nextConfirmation);

        if (resultsMatch && confirmationMatch && !setup.CannonCor?.inputs) {
            return;
        }

        const nextCannonCor = {
            ...(setup.CannonCor || {}),
            results: nextResults,
            confirmation: nextConfirmation
        };
        delete nextCannonCor.inputs;

        applySetupUpdateLocally(setup.Id, {
            CannonCor: nextCannonCor
        });
        UpdateSetup(setup, {
            CannonCor: nextCannonCor
        });
    }, [applySetupUpdateLocally]);

    const persistCannonCorResultFields = useCallback((setup, nextResults) => {
        if (!setup || !nextResults) {
            return;
        }

        const existingResults = setup.CannonCor?.results || {};
        const mergedResults = {
            ...existingResults,
            ...nextResults
        };

        if (JSON.stringify(existingResults) === JSON.stringify(mergedResults)) {
            return;
        }

        const nextCannonCor = {
            ...(setup.CannonCor || {}),
            results: mergedResults
        };

        applySetupUpdateLocally(setup.Id, {
            CannonCor: nextCannonCor
        });
        UpdateSetup(setup, {
            CannonCor: nextCannonCor
        });
    }, [applySetupUpdateLocally]);

    const persistCannonCorInputs = useCallback((setup, nextInputs) => {
        if (!setup || !nextInputs) {
            return;
        }

        const existingCannonCor = setup.CannonCor || {};
        const existingInputs = existingCannonCor.inputs || {};
        if (JSON.stringify(existingInputs) === JSON.stringify(nextInputs)) {
            return;
        }

        const nextCannonCor = {
            ...existingCannonCor,
            inputs: nextInputs
        };

        applySetupUpdateLocally(setup.Id, {
            CannonCor: nextCannonCor
        });
        UpdateSetup(setup, {
            CannonCor: nextCannonCor
        });
    }, [applySetupUpdateLocally]);
    const clearCannonCorInputs = useCallback((setup) => {
        if (!setup?.CannonCor?.inputs) {
            return;
        }

        const nextCannonCor = {
            ...setup.CannonCor
        };
        delete nextCannonCor.inputs;

        applySetupUpdateLocally(setup.Id, {
            CannonCor: nextCannonCor
        });
        UpdateSetup(setup, {
            CannonCor: nextCannonCor
        });
    }, [applySetupUpdateLocally]);
    const persistCannonCorInputsToFamily = useCallback((setup, nextInputs, sessionOverride = null, skipSetupId = null) => {
        const resolvedSession = sessionOverride ?? selectedSession;
        const familySetups = getCorFamilySetups(resolvedSession, setup);
        const familyPlateSetup = familySetups.find((familySetup) => IsPlateCorSetup(familySetup)) || null;

        familySetups.forEach((familySetup) => {
            if (!familySetup) {
                return;
            }

            if (familyPlateSetup && familySetup.Id === familyPlateSetup.Id) {
                persistCannonCorInputs(familySetup, nextInputs);
                return;
            }

            if (skipSetupId && familySetup.Id === skipSetupId) {
                return;
            }

            clearCannonCorInputs(familySetup);
        });
    }, [clearCannonCorInputs, getCorFamilySetups, persistCannonCorInputs, selectedSession]);

    const handleCannonCorComputed = useCallback((payload) => {
        if (!selectedSession || !selectedSetup || viewOnly || isCannonCorConfirmationPending) {
            return;
        }

        if (IsPlateCorSetup(selectedSetup)) {
            persistCannonCorResultFields(selectedSetup, {
                faceDeformationDelta: payload.results?.faceDeformationDelta ?? '',
                faceDeformationStart: payload.results?.faceDeformationStart ?? '',
                faceDeformationEnd: payload.results?.faceDeformationEnd ?? ''
            });
            persistCannonCorInputsToFamily(selectedSetup, payload.inputs || {}, selectedSession);
            return;
        }

        if (!payload.meta?.corReady) {
            persistCannonCorResultFields(selectedSetup, {
                faceDeformationDelta: payload.results?.faceDeformationDelta ?? '',
                faceDeformationStart: payload.results?.faceDeformationStart ?? '',
                faceDeformationEnd: payload.results?.faceDeformationEnd ?? ''
            });
            persistCannonCorInputsToFamily(selectedSetup, payload.inputs || {}, selectedSession, selectedSetup.Id);
            return;
        }

        persistCannonCorPayload(selectedSetup, payload);
        persistCannonCorInputsToFamily(selectedSetup, payload.inputs || {}, selectedSession, selectedSetup.Id);
    }, [selectedSession, selectedSetup, viewOnly, isCannonCorConfirmationPending, persistCannonCorPayload, persistCannonCorInputsToFamily, persistCannonCorResultFields]);


    const pointsKey = React.useMemo(
        () => `topdown-userpoints:${selectedSession?.id ?? 'no-session'}`,
    [selectedSession?.id]
    );

    const summaryRowKeyGetter = useCallback((row) => row.id, []);
    const summaryGridClassName = theme === 'dark' ? 'rdg-dark' : 'rdg-light';
    const summaryGridBackground = useMemo(() => {
        if (typeof window === 'undefined' || !window.document) {
            return 'transparent';
        }

        const value = getComputedStyle(document.documentElement).getPropertyValue('--background-secondary');
        return value ? value.trim() : value;
    }, [theme]);


    //desktop settings
    const [maxamizePanelState, setMaxamizePanelState] = useState(false);
    const [maxamizePanelView, setMaxamizePanelView] = useState('');
    const [threeDPanelDiv, setThreeDPanelDiv] = useState('miniTraj');
    const [threeDKey, setThreeDKey] = useState(0);
    const [leftPanelState, setLeftPanelState] = useState(false);
    const [leftTabSelection, setLeftTabSelection] = useState('TRAJ');
    const [miniWidgetKey, setMiniWidgetKey] = useState(0);
    const [topDownKey, setTopDownKey] = useState(0);
    const [rightPanelState, setRightPanelState] = useState(false);
    const [rightBarSelection, setRightBarSelection] = useState('');
    const [showShotList, setShowShotList] = useState(true);
    const [showShotCopyList, setShowShotCopyList] = useState(true);
    const [resetSessionOpen, setResetSessionOpen] = useState(false);
    const [showSetupSummary, setShowSetupSummary] = useState(false);
    const [processedShotsOpen, setProcessedShotsOpen] = useState(false);
    const [currentProcessedSession, setCurrentProcessedSession] = useState('');
    const [impactOutsideModalOpen, setImpactOutsideModalOpen] = useState(false);
    const [impactOutsideModalData, setImpactOutsideModalData] = useState({
        shotNum: null,
        setupName: '',
        targetName: '',
        dxMm: 0,
        dyMm: 0
    });
    const [setupToleranceModalOpen, setSetupToleranceModalOpen] = useState(false);
    const [setupToleranceModalData, setSetupToleranceModalData] = useState({
        shotNum: null,
        setupName: ''
    });

    const researchQuadConnected = useMemo(() => {
        if (Array.isArray(selectedSession?.ConnectedLMs)) {
            return selectedSession.ConnectedLMs.includes('RESEARCH_QUAD');
        }

        if (!selectedSession?.SessionSetups) {
            return false;
        }

        return selectedSession.SessionSetups.some((sessionSetup) =>
            sessionSetup?.Shots?.some((shot) =>
                shot?.LMInfos?.some((lmInfo) => lmInfo?.LMType === 'RESEARCH_QUAD')
            )
        );
    }, [selectedSession]);
    const showCannonTab = useMemo(() => {
        if (sessionUsesCorCannon) {
            return true;
        }

        if (!Array.isArray(selectedSession?.SessionSetups)) {
            return false;
        }

        return selectedSession.SessionSetups.some((setup) =>
            !!setup?.TestParameters?.CORType || setupHasCorShotShape(setup)
        );
    }, [selectedSession, sessionUsesCorCannon, setupHasCorShotShape]);
    const plateCorSetup = useMemo(
        () => getPlateSetupFromSession(selectedSession, selectedSetup),
        [selectedSession, selectedSetup, getPlateSetupFromSession]
    );

    const [shotInProgress, setShotInProgress] = useState(false);
    const timerRef = useRef(null);
    const autoAdvanceCountsRef = useRef(new Map());
    const lastAutoAdvanceSetupIdRef = useRef(null);
    const selectedSetupCheckedVersion =
        selectedSetup?.Shots?.map((shot) => (shot?.IsChecked ? 1 : 0)).join("") ?? "";
    const confirmationSetup = useMemo(() => {
        if (!cannonCorConfirmation.setupId || !Array.isArray(selectedSession?.SessionSetups)) {
            return null;
        }

        return selectedSession.SessionSetups.find((setup) => setup?.Id === cannonCorConfirmation.setupId) || null;
    }, [cannonCorConfirmation.setupId, selectedSession]);
    const activeCannonCorSetup = cannonCorConfirmation.open
        ? (confirmationSetup || selectedSetup)
        : selectedSetup;
    const currentCannonCorPayload = useMemo(
        () => buildResearchQuadCorPayload({
            masses: researchQuadMasses,
            selectedSetup: activeCannonCorSetup,
            plateSetup: plateCorSetup
        }),
        [researchQuadMasses, activeCannonCorSetup, plateCorSetup]
    );
    const currentCannonCorSignature = currentCannonCorPayload.meta?.confirmationSignature || '';
    const currentCannonCorConfirmed = useMemo(() => {
        const confirmation = activeCannonCorSetup?.CannonCor?.confirmation;

        return (
            confirmation?.confirmed === true &&
            confirmation?.signature === currentCannonCorSignature
        );
    }, [activeCannonCorSetup?.CannonCor?.confirmation, currentCannonCorSignature]);
    const disableCannonCorMassInputs =
        (isCannonCorConfirmationPending && !cannonCorConfirmation.editable) ||
        currentCannonCorConfirmed;
    const handleTopDownSelectShotNumber = (shotNum) => {
        const shot = selectedSetup?.Shots?.find(s => s.ShotNumber === shotNum);
        if (shot) setSelectedShot(shot);
    };
    let isLoaded = false;

    useEffect(() => {
        try {
            const raw = sessionStorage.getItem(pointsKey);
            setUserPoints(raw ? JSON.parse(raw) : []);
        } catch {
            setUserPoints([]);
        }
    }, [pointsKey]);

    // Save on change
    useEffect(() => {
        try {
        sessionStorage.setItem(pointsKey, JSON.stringify(userPoints));
        } catch {}
    }, [pointsKey, userPoints]);

    useEffect(() => {
        const nextMasses = getMassesFromSetup(activeCannonCorSetup);

        setResearchQuadMasses((prev) => {
            if (
                prev.plate === nextMasses.plate &&
                prev.plateBall === nextMasses.plateBall &&
                prev.head === nextMasses.head &&
                prev.headBall === nextMasses.headBall &&
                prev.faceDeformationDelta === nextMasses.faceDeformationDelta &&
                prev.faceDeformationStart === nextMasses.faceDeformationStart &&
                prev.faceDefomationEnd === nextMasses.faceDeformationEnd 
            ) {
                return prev;
            }
            return nextMasses;
        });
    }, [selectedSession?.id, activeCannonCorSetup?.Id, activeCannonCorSetup?.CannonCor, getMassesFromSetup]);

    useEffect(() => {
        if (!cannonCorConfirmation.open) {
            return;
        }

        if (!confirmationSetup) {
            setCannonCorConfirmation({
                open: false,
                editable: false,
                setupId: null,
                error: ''
            });
        }
    }, [cannonCorConfirmation, confirmationSetup]);

    useEffect(() => {
        if (showCannonTab || rightBarSelection !== 'CANNON') {
            return;
        }

        setRightBarSelection('');
        setRightPanelState(false);
        localStorage.setItem('desktop_rightPanelState', false);
    }, [showCannonTab, rightBarSelection]);

    useEffect(() => {
        if (
            !selectedSetup ||
            viewOnly ||
            cannonCorConfirmation.open ||
            IsPlateCorSetup(selectedSetup) ||
            !isResearchQuadCorSetup(selectedSetup) ||
            !currentCannonCorPayload.meta.corReady ||
            currentCannonCorConfirmed
        ) {
            return;
        }

        setRightBarSelection('CANNON');
        setRightPanelState(true);
        setCannonCorConfirmation({
            open: true,
            editable: false,
            setupId: selectedSetup.Id,
            error: ''
        });
    }, [
        selectedSetup,
        viewOnly,
        cannonCorConfirmation.open,
        isResearchQuadCorSetup,
        currentCannonCorPayload,
        currentCannonCorConfirmed
    ]);

    useEffect(() => {
        const loadTaskDataForSession = async () => {
            const sessionTaskId = selectedSession?.TaskID;

            if (!sessionTaskId) {
                setTaskIdData(null);
                return;
            }

            try {
                const loadedTaskData = await getTaskInfo(sessionTaskId);
                setTaskIdData(loadedTaskData);
            } catch (error) {
                console.error(`Failed to load task data for session task ${sessionTaskId}`, error);
                setTaskIdData(null);
            }
        };

        loadTaskDataForSession();
    }, [selectedSession?.TaskID, setTaskIdData]);
    useEffect(() => {
        let session = GetCurrentSession();
        SetUseSeries(CurrentSessionHasBallSeries());
        //setSelectedSession(session);
        //getUniqueLMs(session);

        const leftStateLoaded = localStorage.getItem('desktop_leftPanelState');
        const leftState = leftStateLoaded ? (leftStateLoaded === 'true') : true;
        localStorage.setItem('desktop_leftPanelState', leftState);
        setLeftPanelState(leftState);

        const leftTabLoaded = localStorage.getItem('desktop_leftTabSelection');
        if(leftTabLoaded){
            localStorage.setItem('desktop_leftTabSelection', leftTabLoaded);
            setLeftTabSelection(leftTabLoaded);
        }else{
            localStorage.setItem('desktop_leftTabSelection', "TRAJ");
            setLeftTabSelection("TRAJ");
        }

        const rightStateLoaded = localStorage.getItem('desktop_rightPanelState');
        const rightState = rightStateLoaded ? (rightStateLoaded === 'true') : true;
        localStorage.setItem('desktop_rightPanelState', rightState);
        setRightPanelState(rightState);
        if(rightState){

            setRightBarSelection(rightBarSelection || 'SETUPS');
        }

        const handleBeforeUnload = () => {
            sessionStorage.removeItem('pushedResumeModal');
        };

        if(session && session.SessionSetups){
            let selectedSetup = session.SessionSetups.find(s => s.IsSelected);
           // if(selectedSetup){
           //     //setSelectedSetup(selectedSetup);
           //     SetCurrentSetup(selectedSetup.Id,session)
           if(!selectedSetup && session.SessionSetups.length > 0){
                selectedSetup = session.SessionSetups[0];
                session_update(session.id, selectedSetup.Id, viewOnly);
            }
            const activeSetupId = selectedSetup ? selectedSetup.Id : null;
            if(activeSetupId){
                const normalizedSession = normalizeSessionSelection(session, activeSetupId);
                if(normalizedSession !== session){
                    session = normalizedSession;
                    SetCurrentSession(normalizedSession);
                    selectedSetup = normalizedSession.SessionSetups.find(s => s.Id === activeSetupId) || selectedSetup;
                }

                const nextMasses = getMassesFromSetup(selectedSetup, session);
                setResearchQuadMasses(nextMasses);
                SetCurrentSetup(activeSetupId, session);
                session_update(session.id, activeSetupId, viewOnly);
            }
            else {
                setResearchQuadMasses(emptyResearchQuadMasses);
                session_update(session.id, "no_setup");
            }
        }
        else{
            setResearchQuadMasses(emptyResearchQuadMasses);
            if(session)
                session_update(session.id, "no_setup");
        }
        setSelectedSession(session);
        getUniqueLMs(session);
        setLoaded(true);
        const event = new CustomEvent('sessionLoaded', { detail: true });
        window.dispatchEvent(event);

        window.addEventListener('sessionUpdate', handleSessionRefresh);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('sessionUpdate', handleSessionRefresh);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            
        }
    }, [index]);

    useEffect(() => {
        if(selectedSession){
            const hasUnprocessedData = doesSessionHaveUnprocessedTTData(selectedSession);
            const LoggedUserRole = sessionStorage.getItem('dataCollectorRole');
            if( currentProcessedSession !== selectedSession.id &&
                (LoggedUserRole == "Admin" || LoggedUserRole == "Analyst")
            ){
                setProcessedShotsOpen(hasUnprocessedData);
                setCurrentProcessedSession(selectedSession.id);
            }
        }
        
      }, [selectedSession]);

      useEffect(() => {
        const handleResize = () => {
            setOrientation(detectOrientation());
          };

        setIsMobile(detectDeviceType() === "Mobile");
        const getCurrentDevSetup = () => {
            const currentSess = GetCurrentSession();
            let currSetup = null;

            if(currentSess?.SessionSetups){
                let selectedSetup = currentSess.SessionSetups.find(s => s.IsSelected);
                if(selectedSetup){
                    currSetup = selectedSetup;
                }
                else if(currentSess.SessionSetups.length > 0){
                    currSetup = currentSess.SessionSetups[0];
                }
            }

            return { currentSess, currSetup };
        };

        const isCurrentSessionCORLike = (currentSess) => {
            const isCorTask =
                !!taskIdData &&
                (
                    taskIdData.cor_use_hot_spot ||
                    taskIdData.cor_use_center
                );

            const hasCorShotShape = !!currentSess?.SessionSetups?.some((setup) =>
                setup?.Shots?.some((shot) =>
                    shot?.LMInfos?.some((lmInfo) => {
                        if (lmInfo?.LMType !== 'RESEARCH_QUAD') {
                            return false;
                        }

                        const measurements = {
                            ...(lmInfo.Measurements || {}),
                            ...(lmInfo.CalculatedMeasurements || {})
                        };

                        return Object.keys(measurements).some((key) => key.includes('post'));
                    })
                )
            );

            return isCorTask || hasCorShotShape;
        };

        window.addEventListener('resize', handleResize);
        window.fakeShot = async () => {
            if(getBuildType() !== "DEV"){
                console.error("You may only simulate a shot in DEV.");
                return;
            }

            const { currentSess, currSetup } = getCurrentDevSetup();

            if(currentSess && currSetup){
                var IHorz,
                    IVert,
                    Path,
                    Face,
                    Attack,
                    Loft;
                const bb = isCurrentSessionCORLike(currentSess);//researchQuadConnected && ;

                if(bb){
                    await EmulateCORShot(currentSess, currSetup.Id);
                }
                    else{
                            await EmulateShot(currentSess, currSetup.Id,IHorz,IVert,Path,Face,Attack,Loft);
                        }
            }
          };

          window.fakeRandomShot = async (limits) => {
            if(getBuildType() !== "DEV"){
                console.error("You may only simulate a shot in DEV.");
                return;
            }

            const { currentSess, currSetup } = getCurrentDevSetup();

            if(currentSess && currSetup){
                if(isCurrentSessionCORLike(currentSess)){
                    await EmulateRandomCORShot(currentSess, currSetup.Id, limits);
                }
                else{
                    await EmulateRandomShot(currentSess, currSetup.Id, limits);
                }
            }
          };

          window.fakerandomshot = window.fakeRandomShot;

          window.TestShotInProgress = (value = true) => {
            setShotInProgress(value);
          };

          window.DownloadPDF = async () => {
            DownloadPDF();
          }

          window.setTheme = (theme = "dark") => {
            setAppTheme(theme);
          }

        return () => {
            window.removeEventListener('resize', handleResize);
            delete window.fakeShot;
            delete window.fakeRandomShot;
            delete window.fakerandomshot;
            delete window.TestShotInProgress;
          };
      }, [researchQuadConnected, taskIdData, setAppTheme, applyShotSelection, selectedShot, selectedShotSetupId]);

      useEffect(() => {
        if(selectedSession)
            setSelectedSession(selectedSession);

        if(selectedSetup)
            setSelectedSetup(selectedSetup);

        applyShotSelection(selectedShot, selectedShotSetupId);
        forceMiniWidgetUpdate();

      }, [maxamizePanelState]);

      useEffect(() => {
        replacingShotRef.current = replacingShot;
      }, [replacingShot]);

    const toggleDrawer = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setDrawerOpen(open);
    };

    const toggleSettings = (open) => (event) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setSettingsOpen(open);
    };

    //function SetCurrentSetup(id){
    //    if(!selectedSession || !selectedSession.SessionSetups){
        function SetCurrentSetup(id, sessionOverride = null){
        const sessionSource = sessionOverride ?? selectedSession;

        if(!sessionSource || !sessionSource.SessionSetups){
            return;
        }

        if(id !== ""){
            //let setup = selectedSession.SessionSetups.find(s => s.Id === id);
            let setup = sessionSource.SessionSetups.find(s => s.Id === id);
            forceTopDownUpdate();
            setSelectedSetup(setup);
            if(selectedShot && selectedShotSetupId === id && setup && setup.Shots){
                const newShot = setup.Shots.find(s => s.ShotNumber == selectedShot.ShotNumber);
                applyShotSelection(newShot ?? null, newShot ? id : null);
            }else if(selectedShotSetupId !== id){
                applyShotSelection(null);
            }
        }else{
            forceTopDownUpdate();
            setSelectedSetup(null);
            applyShotSelection(null);
        }
    }

    const handleSetupSelectionFromModal = useCallback((id) => {
        SetCurrentSetup(id);
    }, []);

    const handleSetupSelectionConfirm = useCallback((_result) => {
        setSetupSelectionOpen(false);
    }, []);

    const handleSetupSelectionCancel = useCallback(() => {
        setSetupSelectionOpen(false);
    }, []);

    const handleCannonCorConfirmationEdit = useCallback(() => {
        setCannonCorConfirmation((prev) => ({
            ...prev,
            editable: true,
            error: ''
        }));
    }, []);

    const handleCannonCorConfirmationClose = useCallback(() => {
        setCannonCorConfirmation({
            open: false,
            editable: false,
            setupId: null,
            error: ''
        });
    }, []);

    const handleCannonCorConfirmationAccept = useCallback(() => {
        if (!activeCannonCorSetup || !confirmationSetup) {
            handleCannonCorConfirmationClose();
            return;
        }

        if (!currentCannonCorPayload.meta.corReady) {
            setCannonCorConfirmation((prev) => ({
                ...prev,
                error: 'Enter valid masses before accepting.'
            }));
            return;
        }

        const nextCannonCor = {
            ...(activeCannonCorSetup.CannonCor || {}),
            inputs: currentCannonCorPayload.inputs || {},
            results: currentCannonCorPayload.results || {},
            confirmation: {
                confirmed: true,
                signature: currentCannonCorPayload.meta?.confirmationSignature || ''
            }
        };
        applySetupUpdateLocally(activeCannonCorSetup.Id, {
            CannonCor: nextCannonCor
        });
        UpdateSetup(activeCannonCorSetup, {
            CannonCor: {
                ...nextCannonCor
            }
        });
        handleCannonCorConfirmationClose();
    }, [
        activeCannonCorSetup,
        confirmationSetup,
        currentCannonCorPayload,
        applySetupUpdateLocally,
        handleCannonCorConfirmationClose
    ]);

    const handleSessionRefresh = async (data) =>{
        let sessionData = JSON.parse(data.detail);
        switch(sessionData.Type){
            case "shotStarted":
                setShotInProgress(true);
                startTimer();
                break;
            case "newShot":
                setShotInProgress(false);
                stopTimer();
                if(replacingShotRef.current){
                    const replacedData = await ReplaceShot(sessionData.session_id, sessionData.setup_id, sessionData.shot_num, replacingShotRef.current.ShotNumber);
                    handleReplacingShot(null);
                    let replacedSession = replacedData.session;
                    let replacedSetup = replacedData.setup;
                    let replacedShot = replacedData.shot;

                    if (shouldUseImpactTargetingForSetup(replacedSetup)) {
                        const impactDelta = replacedSetup && replacedShot ? getImpactDeltaForShot(replacedSetup, replacedShot, replacedSession?.TestParameters?.RobotFaceMap) : null;
                        const impactTargetHalfMm = getImpactTargetHalfMm(
                            replacedSetup?.TestParameters?.RobotFaceMap,
                            replacedSession?.TestParameters?.RobotFaceMap
                        );
                        const isOutsideTarget = impactDelta
                            ? (Math.abs(impactDelta.dxMm) > impactTargetHalfMm || Math.abs(impactDelta.dyMm) > impactTargetHalfMm)
                            : false;

                        if (isOutsideTarget && replacedSetup && replacedShot) {
                            if (replacedShot.IsChecked) {
                                const updatedShots = replacedSetup.Shots.map(shot =>
                                    shot.ShotNumber === replacedShot.ShotNumber
                                        ? { ...shot, IsChecked: false }
                                        : shot
                                );
                                replacedSetup = { ...replacedSetup, Shots: updatedShots };
                                replacedSession = {
                                    ...replacedSession,
                                    SessionSetups: replacedSession.SessionSetups.map(setup =>
                                        setup.Id === replacedSetup.Id ? replacedSetup : setup
                                    )
                                };
                                replacedShot = updatedShots.find(shot => shot.ShotNumber === replacedShot.ShotNumber) || replacedShot;
                                await UpdateShotCheck(replacedSetup, replacedShot, false, true);
                            }

                            setImpactOutsideModalData({
                                shotNum: replacedShot.ShotNumber,
                                setupName: GetSetupHeader(replacedSetup),
                                targetName: impactDelta.targetName,
                                dxMm: impactDelta.dxMm,
                                dyMm: impactDelta.dyMm
                            });
                            setImpactOutsideModalOpen(true);
                        }
                    }

                    const setupToleranceResult = getSetupToleranceResult(replacedSession, replacedSetup, replacedShot);
                    if (setupToleranceResult?.allPass) {
                        setSetupToleranceModalData({
                            shotNum: replacedShot.ShotNumber,
                            setupName: GetSetupHeader(replacedSetup)
                        });
                        setSetupToleranceModalOpen(true);
                    }

                    setSelectedSession(replacedSession);
                    setSelectedSetup(replacedSetup);
                    const replacedSetupId = replacedSetup && replacedSetup.Id ? replacedSetup.Id : null;
                    applyShotSelection(replacedShot, replacedSetupId);

                    session_update(replacedData.session.id, replacedData.setup.Id, viewOnly);//Update the app to make sure there is parity
                }else{
                    setShotPreprocessing(true);
                    let updatedSession = await LoadSession(sessionData.session_id);
                    let updatedSetup = updatedSession.SessionSetups.find(setup => setup.Id === sessionData.setup_id) || null;
                    const previousSetup = selectedSession?.SessionSetups?.find((setup) => setup.Id === sessionData.setup_id) || null;
                    const activeMasses =
                        selectedSetup?.Id === sessionData.setup_id
                            ? researchQuadMasses
                            : getMassesFromSetup(previousSetup);
                    const previousCorPayload = buildCorPayloadForSetup(selectedSession, previousSetup, activeMasses);
                    const nextCorPayload = buildCorPayloadForSetup(updatedSession, updatedSetup, activeMasses);
                    const updatedConfirmation = updatedSetup?.CannonCor?.confirmation;
                    const nextSignature = nextCorPayload.meta?.confirmationSignature || '';
                    const nextCorConfirmed =
                        updatedConfirmation?.confirmed === true &&
                        updatedConfirmation?.signature === nextSignature;
                    const shouldPromptForCannonCorConfirmation =
                        !viewOnly &&
                        !IsPlateCorSetup(updatedSetup) &&
                        (sessionUsesCorCannon || isResearchQuadCorSetup(updatedSetup)) &&
                        nextCorPayload.meta.corReady &&
                        !nextCorConfirmed;
                    if (getBuildType() === "DEV") {
                        console.log('[COR confirm gate]', {
                            setupId: sessionData.setup_id,
                            shotNumber: sessionData.shot_num,
                            sessionUsesCorCannon,
                            isResearchQuadCorSetup: isResearchQuadCorSetup(updatedSetup),
                            previousCorReady: previousCorPayload.meta.corReady,
                            nextCorReady: nextCorPayload.meta.corReady,
                            previousPlateCount: previousCorPayload.meta.plateShotCount,
                            previousHeadCount: previousCorPayload.meta.headShotCount,
                            nextPlateCount: nextCorPayload.meta.plateShotCount,
                            nextHeadCount: nextCorPayload.meta.headShotCount,
                            previousSelectedShotCount: previousCorPayload.meta.selectedShotCount,
                            nextSelectedShotCount: nextCorPayload.meta.selectedShotCount,
                            massesOk: nextCorPayload.meta.massesOk,
                            nextCorConfirmed,
                            viewOnly,
                            shouldPromptForCannonCorConfirmation
                        });
                    }
                    let newShot = updatedSetup && updatedSetup.Shots ? updatedSetup.Shots.find(shot => shot.ShotNumber == sessionData.shot_num) : null;
                    

                    if (shouldUseImpactTargetingForSetup(updatedSetup)) {
                        const impactDelta = updatedSetup && newShot ? getImpactDeltaForShot(updatedSetup, newShot, updatedSession?.TestParameters?.RobotFaceMap) : null;
                        const impactTargetHalfMm = getImpactTargetHalfMm(
                            updatedSetup?.TestParameters?.RobotFaceMap,
                            updatedSession?.TestParameters?.RobotFaceMap
                        );
                        const isOutsideTarget = impactDelta
                            ? (Math.abs(impactDelta.dxMm) > impactTargetHalfMm || Math.abs(impactDelta.dyMm) > impactTargetHalfMm)
                            : false;
                        
                        if (isOutsideTarget && updatedSetup && newShot) {
                            if (newShot.IsChecked) {
                                const updatedShots = updatedSetup.Shots.map(shot =>
                                    shot.ShotNumber === newShot.ShotNumber
                                        ? { ...shot, IsChecked: false }
                                        : shot
                                );
                                updatedSetup = { ...updatedSetup, Shots: updatedShots };
                                updatedSession = {
                                    ...updatedSession,
                                    SessionSetups: updatedSession.SessionSetups.map(setup =>
                                        setup.Id === updatedSetup.Id ? updatedSetup : setup
                                    )
                                };
                                newShot = updatedShots.find(shot => shot.ShotNumber === newShot.ShotNumber) || newShot;
                                await UpdateShotCheck(updatedSetup, newShot, false, true);
                                
                            }

                            setImpactOutsideModalData({
                                shotNum: newShot.ShotNumber,
                                setupName: GetSetupHeader(updatedSetup),
                                targetName: impactDelta.targetName,
                                dxMm: impactDelta.dxMm,
                                dyMm: impactDelta.dyMm
                            });
                            setImpactOutsideModalOpen(true);
                        }
                    }

                    const setupToleranceResult = getSetupToleranceResult(updatedSession, updatedSetup, newShot);
                    if (setupToleranceResult?.allPass) {
                        setSetupToleranceModalData({
                            shotNum: newShot.ShotNumber,
                            setupName: GetSetupHeader(updatedSetup)
                        });
                        setSetupToleranceModalOpen(true);
                    }

                    setSelectedSession(updatedSession);
                    setSelectedSetup(updatedSetup);
                    const updatedSetupId = updatedSetup && updatedSetup.Id ? updatedSetup.Id : null;
                    applyShotSelection(newShot, updatedSetupId);
                    handleReplacingShot(null);
                    if (shouldPromptForCannonCorConfirmation && updatedSetupId) {
                        setRightBarSelection('CANNON');
                        setRightPanelState(true);
                        setCannonCorConfirmation({
                            open: true,
                            editable: false,
                            setupId: updatedSetupId,
                            error: ''
                        });
                    }

                    if(UseSeries && updatedSetup.Id !== "Setup Series" && sessionData.shot_num < updatedSession.BallSeries.length){
                        //I dont do shot_num + 1 because shot_num isn't like arrays, it starts at 1.
                        // let nextGroup = updatedSession.BallSeries[(sessionData.shot_num) % updatedSession.BallSeries.length];
                        setPostBallSeriesShotModalOpen(true);
                    }else if(UseSeries && updatedSetup.Id !== "Setup Series" && sessionData.shot_num == updatedSession.BallSeries.length){
                        setPostBallSeriesShotEndedModalOpen(true);
                    }
                    setShotPreprocessing(false);
                }
            break;
            case "refresh":
                const refreshedSession = await LoadSession(sessionData.session_id);
                setSelectedSession(refreshedSession);

                const explicitSetupId = sessionData.setup_id && sessionData.setup_id !== '' ? sessionData.setup_id : null;
                const targetSetupId = explicitSetupId || selectedShotSetupId || (selectedSetup ? selectedSetup.Id : null);
                const refreshedSetup = targetSetupId ? refreshedSession.SessionSetups.find(setup => setup.Id === targetSetupId) || null : null;
                setSelectedSetup(refreshedSetup);

                if(selectedShot && selectedShotSetupId){
                    const owningSetup = refreshedSession.SessionSetups.find(setup => setup.Id === selectedShotSetupId) || null;
                    const refreshedShot = owningSetup && owningSetup.Shots ? owningSetup.Shots.find(shot => shot.ShotNumber == selectedShot.ShotNumber) : null;
                    const owningSetupId = owningSetup && owningSetup.Id ? owningSetup.Id : null;
                    applyShotSelection(refreshedShot, owningSetupId);
                }else{
                    applyShotSelection(null);
                }
                break;
        }
    }

    const INCH_TO_MM = 25.4;
    const SETUP_TOLERANCE_FIELDS = {
        ImpactAttackAngleDeg: { targetKey: 'AttackTarget', toleranceKey: 'AttackTolerance' },
        ImpactPathAngleDeg: { targetKey: 'PathTarget', toleranceKey: 'PathTolerance' },
        FaceToPath: { targetKey: 'FaceToPathTarget', toleranceKey: 'FaceToPathTolerance' },
        ImpactPitchAngleDeg: { targetKey: 'DynamicLoftTarget', toleranceKey: 'DynamicLoftTolerance' },
        ImpactRollAngleDeg: { targetKey: 'DynamicLieTarget', toleranceKey: 'DynamicLieTolerance' }
    };
    const SETUP_TOLERANCE_KEYS = Object.keys(SETUP_TOLERANCE_FIELDS);

    const getImpactDeltaForShot = (setup, shot, robotFaceMap = null) => {
        const target = setup?.TestParameters?.RobotFaceMap;
        const targetX = Number(target?.y);
        const targetY = Number(target?.z);
        const { xKey, yKey } = getImpactMeasurementKeys(target, robotFaceMap);
        if (!Number.isFinite(targetX) || !Number.isFinite(targetY)) {
            return null;
        }

        const lmInfo = shot?.LMInfos?.find((info) => {
            const measurements = getAllMeasurementsForLMInfo(info);
            return measurements[xKey] != null && measurements[yKey] != null;
        });

        if (!lmInfo) {
            return null;
        }
        const measurements = getAllMeasurementsForLMInfo(lmInfo);
        const impactX = Number(measurements[xKey]);
        const impactY = Number(measurements[yKey]);

        if (!Number.isFinite(impactX) || !Number.isFinite(impactY)) {
            return null;
        }

        const dxMm = (impactX - targetX) * INCH_TO_MM;
        const dyMm = (impactY - targetY) * INCH_TO_MM;

        return {
            dxMm,
            dyMm,
            targetName: target?.Name || ''
        };
    };

    const shouldUseImpactTargetingForSetup = (setup) => {
        if (!setup?.TestParameters?.RobotFaceMap) {
            return false;
        }

        const normalizedLocation = (setup?.ClubDescription || setup?.TestParameters?.RobotFaceMap?.Name || '')
            .trim()
            .toLowerCase();

        return normalizedLocation === 'center' || normalizedLocation === 'setup';
    };

    const getSetupToleranceResult = (session, setup, shot) => {
        if (!session || !setup || !shot) {
            return null;
        }

        const isSetup = setup?.TestParameters?.RobotFaceMap?.IsSetup === true;
        if (!isSetup) {
            return null;
        }

        const params = session?.TestParameters?.RobotFaceMap;
        if (!params) {
            return null;
        }

        const measurementMap = {};
        if (Array.isArray(shot?.LMInfos)) {
            shot.LMInfos.forEach((info) => {
                const measurements = {
                    ...(info?.Measurements || {}),
                    ...(info?.CalculatedMeasurements || {})
                };
                SETUP_TOLERANCE_KEYS.forEach((key) => {
                    if (measurementMap[key] === undefined && measurements[key] !== undefined && measurements[key] !== null) {
                        measurementMap[key] = measurements[key];
                    }
                });
            });
        }

        let allPass = true;
        let evaluatedCount = 0;
        SETUP_TOLERANCE_KEYS.forEach((key) => {
            const config = SETUP_TOLERANCE_FIELDS[key];
            const hasValidRobotTestData = isValidRobotTestDataField(params, config.targetKey)
                && isValidRobotTestDataField(params, config.toleranceKey);
            if (!hasValidRobotTestData) {
                return;
            }

            const value = Number(measurementMap[key]);
            const target = Number(params[config.targetKey]);
            const tolerance = Number(params[config.toleranceKey]);
            evaluatedCount += 1;
            const pass = Number.isFinite(value) && Number.isFinite(target) && Number.isFinite(tolerance)
                && Math.abs(value - target) <= tolerance;
            if (!pass) {
                allPass = false;
            }
        });

        if (evaluatedCount === 0) {
            return null;
        }

        return { allPass };
    };

    const startTimer = useCallback((durationMs = 30000) => {
        if (timerRef.current) return;

        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setShotInProgress(false);
        }, durationMs);
    }, []);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
        };
    }, []);

    const getNextSetupAfter = useCallback((session, currentSetupId) => {
        if (!session || !Array.isArray(session.SessionSetups) || !currentSetupId) {
            return null;
        }

        const setups = session.SessionSetups;
        const currentIndex = setups.findIndex(setup => setup && setup.Id === currentSetupId);
        if (currentIndex === -1) {
            return null;
        }

        for (let i = currentIndex + 1; i < setups.length; i++) {
            const candidate = setups[i];
            if (!candidate) {
                continue;
            }
            return candidate;
        }

        return null;
    }, []);

    useEffect(() => {
        autoAdvanceCountsRef.current.clear();
        lastAutoAdvanceSetupIdRef.current = null;
    }, [selectedSession?.id]);

    useEffect(() => {
        const setupId = selectedSetup?.Id;
        if (!setupId) {
            lastAutoAdvanceSetupIdRef.current = null;
            return;
        }

        if (lastAutoAdvanceSetupIdRef.current === setupId) {
            return;
        }

        const currentSession = GetCurrentSession();
        const currentSetup = currentSession?.SessionSetups?.find((setup) => setup?.Id === setupId) || selectedSetup;
        const checkedCount = Array.isArray(currentSetup?.Shots)
            ? currentSetup.Shots.filter((shot) => shot?.IsChecked).length
            : 0;

        autoAdvanceCountsRef.current.set(setupId, checkedCount);
        lastAutoAdvanceSetupIdRef.current = setupId;
    }, [selectedSession?.id, selectedSetup]);

    useEffect(() => {
        if(shotPreprocessing)
        { 
            return;

        }
        if (!selectedSetup) {
            return;
        }

        const currentSession = GetCurrentSession();
        if (!currentSession || !Array.isArray(currentSession.SessionSetups)) {
            return;
        }

        const setupId = selectedSetup?.Id;
        if (!setupId) {
            return;
        }

        const currentSetup = currentSession.SessionSetups.find(setup => setup && setup.Id === setupId) || selectedSetup;
        const robotFaceMap = currentSetup?.TestParameters?.RobotFaceMap;
        if (currentSetup?.TestParameters?.CORType) {
            return;
        }
        if (robotFaceMap?.IsSetup === true) {
            return;
        }
        
        const sessionRobotFaceMap = currentSession?.TestParameters?.RobotFaceMap;
        const requiredHits = Number(sessionRobotFaceMap?.NumberOfHits);
        //const requiredHits = Number(currentSetup.robotFaceMap?.requiredHits);
        //const sessionRobotFaceMap = currentSetup?.robotFaceMap;
        if (!Number.isFinite(requiredHits) || requiredHits <= 0) {
            return;
        }

        const checkedCount = Array.isArray(currentSetup?.Shots)
            ? currentSetup.Shots.filter((shot) => shot?.IsChecked).length
            : 0;

        const lastCount = autoAdvanceCountsRef.current.get(setupId) ?? 0;
        autoAdvanceCountsRef.current.set(setupId, checkedCount);

        if (checkedCount < requiredHits || lastCount >= requiredHits) {
            return;
        }

        const nextSetup = getNextSetupAfter(currentSession, setupId);
        if (!nextSetup) {
            return;
        }

        (async () => {
            await SelectSessionSetup(currentSession, nextSetup, !viewOnly);
            const updatedSession = GetCurrentSession();
            setSelectedSession(updatedSession);
            SetCurrentSetup(nextSetup.Id, updatedSession);
            session_update(updatedSession.id, nextSetup.Id, viewOnly);
        })();
    }, [selectedSession, selectedSetup, selectedSetupCheckedVersion, shotsChangedVersion, viewOnly, getNextSetupAfter,setShotPreprocessing]);

    const handleCurrentSessionRefresh = async () =>{
        if(!selectedSession){
            return;
        }

        let updatedSession = await LoadSession(selectedSession.id);
        setSelectedSession(updatedSession);
        let updatedSessionSetup = selectedSetup ? updatedSession.SessionSetups.find(setup => setup.Id === selectedSetup.Id) || null : null;
        setSelectedSetup(updatedSessionSetup);

        if(selectedShot && selectedShotSetupId){
            const owningSetup = updatedSession.SessionSetups.find(setup => setup.Id === selectedShotSetupId) || null;
            const updatedShot = owningSetup && owningSetup.Shots ? owningSetup.Shots.find(shot => shot.ShotNumber == selectedShot.ShotNumber) : null;
            const owningSetupId = owningSetup && owningSetup.Id ? owningSetup.Id : null;
            applyShotSelection(updatedShot, owningSetupId);
        }else{
            applyShotSelection(null);
        }

        if(updatedSessionSetup && updatedSessionSetup.Id){
            session_update(updatedSession.id, updatedSessionSetup.Id, viewOnly);//Update the app to make sure there is parity
        }else{
            session_update(updatedSession.id, "no_setup", viewOnly);
        }
    }

    function getUniqueLMs(data){
        const uniqueLMTypes = new Set();

        if(!data || !data.SessionSetups){
            setLMDrawing([]);
            return;
        }

        data.SessionSetups.forEach(sessionSetup => {
            // Iterate through the Shots in each SessionSetup

            if(sessionSetup.Shots){
                sessionSetup.Shots.forEach(shot => {
                    // Iterate through the LMInfos in each Shot
                    shot.LMInfos.forEach(lmInfo => {
                        // Add the LMType to the set
                        const lmData = LMList.find(lm => lm.type === lmInfo.LMType);
    
                        if(lmData.hasTraj && lmData.hasTraj){
                            uniqueLMTypes.add(lmInfo.LMType);
                        }
                    });
                });
            }
        });

        const lms = Array.from(uniqueLMTypes);
        if(lms.length > 0){
            setLMDrawing(prev => (prev && lms.includes(prev) ? prev : lms[0] ?? []));
        }
    }

    const handleSwitchedSession = (sess) =>{
        //setSelectedSession(sess);
        let sessionToApply = sess;
        if(sess.SessionSetups){
            let selectedSetup = sess.SessionSetups.find(s => s.IsSelected);
            if(!selectedSetup && sess.SessionSetups.length > 0){
                selectedSetup = sess.SessionSetups[0];
            }
            const activeSetupId = selectedSetup ? selectedSetup.Id : null;
            if(activeSetupId){
                const normalizedSession = normalizeSessionSelection(sess, activeSetupId);
                if(normalizedSession !== sess){
                    sessionToApply = normalizedSession;
                    SetCurrentSession(normalizedSession);
                }

                const resolvedSetup = sessionToApply.SessionSetups.find(s => s.Id === activeSetupId) || selectedSetup;
                SetCurrentSession(sessionToApply);
                setSelectedSession(sessionToApply);
                setSelectedSetup(resolvedSetup);
                const nextMasses = getMassesFromSetup(resolvedSetup, sessionToApply);
                setResearchQuadMasses(nextMasses);
                session_update(sessionToApply.id, activeSetupId, viewOnly);
                return;
            }

            SetCurrentSession(sessionToApply);
            setSelectedSession(sessionToApply);
            setSelectedSetup(null);
            setResearchQuadMasses(emptyResearchQuadMasses);
            session_update(sessionToApply.id, "no_setup");
            return;
                }
        //}
        SetCurrentSession(sessionToApply);
        setSelectedSession(sessionToApply);
        setResearchQuadMasses(emptyResearchQuadMasses);
        session_update(sessionToApply.id, "no_setup");
    }

    const drawerContent = (
        <Box
            sx={{ width: "100%", height: "100%", overflow:"hidden"}}
            role="presentation"
            style={{backgroundColor:getComputedStyle(document.documentElement).getPropertyValue('--background-primary'), overflow:"hidden"}}
            // onClick={toggleDrawer(false)}
            // onKeyDown={toggleDrawer(false)}
        >
            <Setups currentSession={selectedSession} setCurrentSetup={SetCurrentSetup} drawerOpenState={toggleDrawer} handleSessionChange={handleSwitchedSession}/>
        </Box>
    );

    const settingsContent = (
        <Box
            sx={{ width: "100%", height: "100%", overflow:"hidden"}}
            role="presentation"
            style={{backgroundColor:getComputedStyle(document.documentElement).getPropertyValue('--background-primary'), overflow:"hidden"}}
            // onClick={toggleDrawer(false)}
            // onKeyDown={toggleDrawer(false)}
        >
            <Settings setFlyoutState={setSettingsOpen} onLoadSession={onLoadSession} onLoadModal={onLoadModal} drawerOpenState={toggleSettings}/>
        </Box>
    );

    function renderSwitch(){
        switch(currentView)
        {
            case "SHOTLIST":
                return (
                    <ShotListsPanel
                        sessionSetups={selectedSession ? selectedSession.SessionSetups || [] : []}
                        activeSetupId={selectedSetup ? selectedSetup.Id : null}
                        selectedShot={selectedShot}
                        selectedShotSetupId={selectedShotSetupId}
                        onShotSelected={handleShotSelected}
                        handleShotChange={handleCurrentSessionRefresh}
                        replacingShot={replacingShot}
                        setReplacingShot={handleReplacingShot}
                        showFavoritesOnly={showFavoritesOnly}
                        onFavoritesToggle={handleFavoritesToggle}
                        ShowDetailedSummary={showDetailedSummary}
                        onShotsChanged={refreshSetupsFromShots}
                        
                    />
                   
                );
            case "WIDGETS":
                return <Widgets currentShot={selectedShot}/>;
            case "MINIWIDGETS":
                return <MiniWidgets currentShot={selectedShot}/>;
            case "THREEDTRAJ":
                return <ThreeDTraj selectedShot={selectedShot}/>;
            //case "TOPDOWN":
            //   return (<TopDown currentSession={selectedSession} currentSetup={selectedSetup} currentShot={selectedShot} sharedUserPoints={userPoints} onUserPointsChange={setUserPoints}/>);
            case "TOPDOWN":

                return (<TopDown currentSession={selectedSession} currentSetup={selectedSetup} currentShot={selectedShot} sharedUserPoints={userPoints} onUserPointsChange={setUserPoints} onSelectShotNumber={handleTopDownSelectShotNumber}/>);
            

               // return <TopDown currentSession={selectedSession} currentSetup={resolvedShotSetup} currentShot={selectedShot} />;

        }
    }

    function headerRender(){
        if(UseSeries){

            let bgColor = "";
            let shotCount = selectedSetup ? (selectedSetup.Shots ? selectedSetup.Shots.length : 0) : 0;
            let ballSeriesLength = selectedSession ? (selectedSession.BallSeries ? selectedSession.BallSeries.length : 0) : 0
            const setupHeader = selectedSetup ? GetSetupHeader(selectedSetup) : "No Setup selected";
            let subHeader = "";
            if(shotCount < ballSeriesLength){
                subHeader = setupHeader + " | Next Shot: " + selectedSession.BallSeries[shotCount];
                bgColor = getComputedStyle(document.documentElement).getPropertyValue('--background-ball-series-ready');
            }
            else{
                subHeader = setupHeader + " | Series Ended";
                bgColor = getComputedStyle(document.documentElement).getPropertyValue('--background-ball-series-done');
            }

            if(selectedSetup && selectedSetup.Id === "Setup Series"){
                subHeader = "Setup Series";
                bgColor = getComputedStyle(document.documentElement).getPropertyValue('--background-primary');
            }

            return  <div className='headerContainer' style={{backgroundColor:bgColor}}>
                        <div style={{width: "80px", height: "80px", alignContent: "center"}}>
                            <button onClick={toggleSettings(true)} style={{backgroundColor:"transparent", border: 0}} >{<FlyoutButtonIcon/>}</button>
                        </div>
                        <div style={{width:"calc(100% - 120px)", height: "100%"}}>
                            <h2 className='headerSessionName'>{loaded ? selectedSession.Description : ""}</h2>
                            <h4 className='headerSetupName'>{subHeader}</h4>
                        </div>
                        <div style={{width: "80px", height: "80px", alignContent: "center"}}>
                            <button onClick={toggleDrawer(true)} style={{backgroundColor:"transparent", border: 0}} >{<SetupsFlyoutButtonIcon/>}</button>
                        </div>
                    </div>;
        }
        else
        {
            return  <div className='headerContainer'>
                        <div style={{width: "80px", height: "80px", alignContent: "center"}}>
                            <button onClick={toggleSettings(true)} style={{backgroundColor:"transparent", border: 0}} >{<FlyoutButtonIcon/>}</button>
                        </div>
                        <div style={{width:"calc(100% - 120px)", height: "100%"}}>
                            <h2 className='headerSessionName-mobile'>{loaded ? GetSessionHeader(selectedSession) : ""}</h2>
                            <h4 className='headerSetupName-mobile'>{loaded ? (selectedSetup ? GetSetupHeader(selectedSetup) : "No Setup selected") : "No Setup selected"}</h4>
                        </div>
                        <div style={{height:'100%'}}>
                            <button onClick={toggleDrawer(true)} style={{backgroundColor:"transparent", border: 0, height:'100%'}} >{<SetupsFlyoutButtonIcon width={"40px"} height={"40px"}/>}</button>
                        </div>
                    </div>;
        }
    }

    function changeView(newView){
        setCurrentView(newView);
    }

    //Desktop UI function
    function getDesktopDescription(){
        if(UseSeries){

            let bgColor = "";
            let shotCount = selectedSetup ? (selectedSetup.Shots ? selectedSetup.Shots.length : 0) : 0;
            let ballSeriesLength = selectedSession ? (selectedSession.BallSeries ? selectedSession.BallSeries.length : 0) : 0
            const setupHeader = selectedSetup ? GetSetupHeader(selectedSetup) : "No Setup selected";
            let subHeader = "";
            if(shotCount < ballSeriesLength){
                subHeader = setupHeader + " | Next Shot: " + selectedSession.BallSeries[shotCount];
                bgColor = getComputedStyle(document.documentElement).getPropertyValue('--background-ball-series-ready');
            }
            else{
                subHeader = setupHeader + " | Series Ended";
                bgColor = getComputedStyle(document.documentElement).getPropertyValue('--background-ball-series-done');
            }

            if(selectedSetup && selectedSetup.Id === "Setup Series"){
                subHeader = "Setup Series";
                bgColor = getComputedStyle(document.documentElement).getPropertyValue('--background-primary');
            }
            return (
            <div className='headerContainer-desktop' style={{backgroundColor:bgColor}}>
                <div style={{width:"100%", height: "100%"}}>
                    <h2 className='headerSessionName'>{loaded ? GetSessionHeader(selectedSession) : ""}</h2>
                    <h4 className='headerSetupName'>{subHeader}</h4>
                </div>
            </div>);
        }else{
            let bgColor;// = getComputedStyle(document.documentElement).getPropertyValue('--background-primary');
            let finished = false;
            if(selectedSession && selectedSession.TaskFinished){
                bgColor = getComputedStyle(document.documentElement).getPropertyValue('--background-ball-series-done');
                finished = true;
            }
            return (
            <div className='headerContainer-desktop' style={{pointerEvents:'auto',  backgroundColor:finished ? bgColor : null}}>
                <div style={{width:"100%", height: "100%", display:'flex', flexDirection:'column', justifyContent:'center'}}>
                    <span className='headerSessionName'>{loaded ? GetSessionHeader(selectedSession) : ""}</span>
                    <span className='headerSetupName'>{loaded ? (selectedSetup ? GetSetupHeader(selectedSetup) : "No Setup selected") : "No Setup selected"}</span>
                </div>
            </div>
            );
        }
    }

    function getMaximizedComponent(){
        switch(maxamizePanelView){
            default:
                return <></>;
            case "WIDGETS":
                return <Widgets currentShot={selectedShot}/>;
            case "THREEDTRAJ":
                return (
                    <></>
                );
            case "TOPDOWN":

                return (<TopDown currentSession={selectedSession} currentSetup={selectedSetup} currentShot={selectedShot} sharedUserPoints={userPoints} onUserPointsChange={setUserPoints} onSelectShotNumber={handleTopDownSelectShotNumber}/>
                );

                //return <TopDown currentSession={selectedSession} currentSetup={resolvedShotSetup} currentShot={selectedShot} />;

        }
    }

    function getRightPanelComponent(){
        switch(rightBarSelection){
            default:
                return <></>;
            case "SETTINGS":
                return <Settings onLoadSession={onLoadSession} onLoadModal={onLoadModal}/>;
            case "OPTIONS":
                return <Options showDetailedSummary={showDetailedSummary}  onDetailedSummaryChange={handleDetailedSummaryChange} />;
            case "CANNON":
                return (
                    <div style={{height:'max-content', overflowY:'auto', overflowX:'hidden', maxHeight:'100vh', padding:'10px'}}>
                            <ResearchQuadMasses
                            visible={researchQuadConnected}
                            selectedSetup={activeCannonCorSetup}
                            plateSetup={plateCorSetup}
                            masses={researchQuadMasses}
                            onMassChange={setResearchQuadMasses}
                            onCorComputed={handleCannonCorComputed}
                            disableInputs={disableCannonCorMassInputs}
                            persistOnChange={!isCannonCorConfirmationPending}
                            forceNotCalculated={isCannonCorConfirmationPending}
                            showPlaceholder
                            corShots={selectedSession?.SessionSetups?.[0]?.shots?.shotCount ?? 0}
                            rows={activeCannonCorSetup ? [activeCannonCorSetup] : []}
                            //plateshots
                            //headshots
                        />
                    </div>
                );
            case "SETUPS":
                return (
                    <div style={{height:'max-content', overflowY:'auto', overflowX:'hidden', maxHeight:'100vh'}}>
                        <div style={{height:'max-content'}}>
                            <SetupsList currentSession={selectedSession} setCurrentSetup={SetCurrentSetup} Disabled={shotInProgress} Refresh={handleCurrentSessionRefresh} showCheckboxes={false} onShotsChanged={registerSetupsShotsChanged}/>
                        </div>
                        <div style={{height:'150px'}}>
                            <LMDisplaySelector currentSession={selectedSession}/>
                        </div>
                    </div>
                );
        }
    }

    function getLeftPanelComponent(){
        switch(leftTabSelection){
            default:
                return <></>;
            case "TRAJ":
                return (
                    <>
                    <div className='desktop-left-container-topdown' onClick={() => handleMaximize("TOPDOWN")}>
                        <div  style={{height:'100%',width:'100%', pointerEvents:'none'}} >

                            <TopDown currentSession={selectedSession} currentSetup={selectedSetup} currentShot={selectedShot} 
                            forcedScale={(getComputedStyle(document.documentElement).getPropertyValue('--desktop-left-panel-size')/1047)} forcedOrigin={{x: 0, y:0}} forceUpdateIndex={topDownKey} sharedUserPoints={userPoints}onUserPointsChange={setUserPoints}/>

                        </div>
                    </div>
                    <div className='desktop-left-container-threed' onClick={() => handleMaximize("THREEDTRAJ")}>
                        <div  style={{pointerEvents:'none'}} >
                            <div id={'miniTraj'} style={{width:'100%', height:'100%'}}></div>
                        </div>
                    </div>
                    </>
                    );
            case "CLUB":
                return (
                    <>
                    <LoftAttack selectedShot={selectedShot} selectedSetup={selectedSetup}/>
                    <FacePath selectedShot={selectedShot} selectedSetup={selectedSetup}/>
                    <ImpactLocation selectedSetup={selectedSetup} selectedShot={selectedShot} sessionDescription={selectedSession?.Description}/>
                    {shouldUseImpactTargetingForSetup(selectedSetup) ? (
                        <ImpactTargetBox key={theme} selectedSetup={selectedSetup} selectedShot={selectedShot} robotFaceMap={selectedSession?.TestParameters?.RobotFaceMap}/>
                    ) : null}
                    </>
                );
        }
    }

    const handleLeftContainer = () => {
        const state = !leftPanelState;
        setLeftPanelState(state);
        localStorage.setItem('desktop_leftPanelState', state);

    }

    const handleMaximize = (view) => {
        setMaxamizePanelState(true);
        setMaxamizePanelView(view);
        if(view === "THREEDTRAJ"){
            setThreeDPanelDiv('megaTraj');
        }

    }

    const handleMaximizeMinMax = () => {
        setThreeDPanelDiv(!maxamizePanelState ? 'megaTraj' : 'miniTraj');
        setMaxamizePanelState(!maxamizePanelState);
    }

    const handleSwapToWidgets = () => {
        setShowSetupSummary(false);
        setShowShotList(prev => !prev);
    }

    const handleToggleSetupSummary = () => {
        setShowSetupSummary((prev) => {
            const next = !prev;
            if (next) {

                setShowShotList(true);
            }
            
            return next;
        });
    }

    const handleOpenSetupSelection = () => {
        setSetupSelectionOpen(true);
    }
    const handleCloseSetupSelection = () => {
        setSetupSelectionOpen(false);
    }

    const forceMiniWidgetUpdate = () => {
        setMiniWidgetKey(prevKey => prevKey + 1);
      };

      const forceTopDownUpdate = () => {
        setTopDownKey(prevKey => prevKey + 1);
      };

    const handleLeftBarTabButtonSelection = (value) => {
        setLeftTabSelection(value);
        setThreeDKey(prevKey => prevKey + 1);
        localStorage.setItem('desktop_leftTabSelection', value);
    }

    const handleRightBarButtonSelection = (value) => {
        if (value === 'CANNON' && !showCannonTab) {
            return;
        }

        if(rightBarSelection === value){
            setRightBarSelection('');
            setRightPanelState(false);
             localStorage.setItem('desktop_rightPanelState', false);
        }else{
            setRightBarSelection(value);
            if(!rightPanelState){
                setRightPanelState(true);
                localStorage.setItem('desktop_rightPanelState', true);
            }
        }
    }

    const handleReplacingShot = (value) =>{
        setReplacingShot(value);
        if(value){
            setReplacingShotNum(value.ShotNumber);
        }else{
            setReplacingShotNum(null);
        }
    }

    
    const newSessionClick = () => {
        setResetSessionOpen(true);
    };

    function newSession(value){
        if(value){
            SetCurrentSession(null);
            ResetSessions();
            onLoadSession(null, "PORTAL");
                session_update('no_session','no_setup');
        }else{
            setResetSessionOpen(false);
        }
    };

    function doesSessionHaveUnprocessedTTData(session) {
        if (!session.SessionSetups || session.SessionSetups.length === 0) {
            return false;
        }
    
        let potentialShots = 0;
        let alreadyProcessedShots = 0;
    
        for (const setup of session.SessionSetups) {
            if (!setup.Shots) {
                continue;
            }
    
            for (const shot of setup.Shots) {
                const LMInfo = shot.LMInfos.find(l => l.LMType === "TOP_TRACER");
    
                if (LMInfo) {
                    potentialShots++;
                }
    
                if (LMInfo && LMInfo.ShotId && LMInfo.Processed) {
                    alreadyProcessedShots++;
                }
            }
        }
    
        return potentialShots !== alreadyProcessedShots;
    };

    const handleProcessShotsClickResult = (result) => {
        setProcessedShotsOpen(false);

        if(result && appState.InApp){
            process_toptracer_shots("process");
        }
    }

    //FOR DEBUGGING!!
    const DownloadPDF = async () => {
        const url = "/PDFbase64.txt";//doesn't exist anymore so be careful

        try {
            const response = await fetch(url);
            if (!response.ok) {
            throw new Error(`Network response was not ok (status ${response.status})`);
            }
            const text = await response.text();
            Save(text, true, "Test.pdf");
            return text;
        } catch (err) {
            console.error("Fetch error:", err);
        }

    }

    const DesktopRender = () => {
        return (
            <div >
                <ThreeDTraj selectedShot={selectedShot} customCanvasName={threeDPanelDiv} key={threeDKey} />
                <div className={`desktop-left-container-items-float ${leftPanelState ? 'open' : ''}`}>
                    <div className='desktop-left-container-tab-container'>
                        <button className={`desktop-left-container-tab-button ${leftTabSelection === "TRAJ" ? "selected" : ""}`} onClick={() => handleLeftBarTabButtonSelection("TRAJ")}>
                            <ThreeDTrajIcon width='36px' height='36px'/>
                            <span className='desktop-left-container-tab-text'>TRAJ</span>
                        </button>
                        <button className={`desktop-left-container-tab-button ${leftTabSelection === "CLUB" ? "selected" : ""}`} onClick={() => handleLeftBarTabButtonSelection("CLUB")}>
                            <FlagIcon width='24px' height='24px'/>
                            <span className='desktop-left-container-tab-text'>CLUB</span>
                            
                        </button>
                    </div>
                    {getLeftPanelComponent()}
                </div>
                <div style={{position:"absolute", pointerEvents:'none'}}>
                    <div className='desktop-container'>
                        <div className={`desktop-left-container ${leftPanelState ? 'open' : ''}`}>
                            
                        </div>
                        <div className={`desktop-left-bar ${leftPanelState ? 'open' : ''}`}>
                            <span className='desktop-left-bar-label'>
                                TRAJECTORY
                            </span>
                            <button className={`desktop-left-bar-button ${leftPanelState ? 'open' : ''}`} onClick={() => handleLeftContainer()}>
                                {leftPanelState && (
                                    <ChevronLeftIcon className='desktop-left-bar-chevron'/>
                                )}
                                <></>
                                {!leftPanelState &&(
                                    <ChevronRightIcon className='desktop-left-bar-chevron'/>
                                )}
                            </button>
                        </div>
                        <div className='desktop-middle-container'>
                            <div className='desktop-header-container' style={{pointerEvents:'auto'}}>
                                <div className='desktop-header-data-container'>
                                    <strong className='desktop-header-data-header-label'>{selectedSession?.EntityType === "Test" ? "Test" : "Golfer"}</strong>
                                    {selectedSession && (
                                        <GolferDisplay CurrentSession={selectedSession} SessionChanged={handleSwitchedSession} Disabled={shotInProgress}/>
                                    )}
                                    {appWorking && (
                                        <div className='desktop-header-working-indicator' aria-label='Working'>
                                            <div className='desktop-header-working-spinner' />
                                        </div>
                                    )}
                                </div>
                                
                                {getDesktopDescription()}

                                {!rightPanelState && (
                                    <div style={{height:'100%', display:'flex', flexDirection:'column', justifyContent:'center',zIndex:'0'}}>
                                        <SetupsCombo currentSession={selectedSession} setCurrentSetup={SetCurrentSetup} Disabled={shotInProgress} Refresh={handleCurrentSessionRefresh}/>
                                    </div>
                                )}
                            </div>
                            
                            <div className='desktop-middle-item-container'>
                                
                                <div className='desktop-widgetshotlist-header-container'>
                                   <div className='desktop-shotlist-favorites-toggle'>
                                        <span onClick={() => handleFavoritesToggle(false)}>Show All</span>
                                        <Switch
                                            size='small'
                                            checked={showFavoritesOnly}
                                            onChange={(e) => handleFavoritesToggle(e.target.checked)}
                                        />
                                        <span onClick={() => handleFavoritesToggle(true)}>Favorites</span>
                                    </div>
                                    <button style={{width:'40px', height:'40px', border: 0, background:'transparent',marginLeft: 'auto'}} onClick={() => handleSwapToWidgets()} >
                                        {showShotList ? (
                                            <WidgetButtonIcon width='30px' height='30px'/>
                                        ) : (
                                            <ShotListButtonIcon width='30px' height='30px'/>
                                        )}
                                    </button>
                                    <button
                                        style={{width:'45px', height:'45px', border: 0, background:'transparent', marginTop: '-3px', marginLeft:'8px', opacity: showSetupSummary ? 1 : 0.7}}
                                        onClick={handleToggleSetupSummary} title='Summary View'
                                    >
                                        <DocumentSummaryIcon width='45px' height='45px' marginTop='0px'/>
                                    </button>
                                    {/*Enable the below to have the ability to select which setups and their shots are shown in the non summary display
                                    <button style={{width:'40px', height:'40px', border: 0, background:'transparent', marginLeft:'8px'}} onClick={() => handleOpenSetupSelection()}>
                                        <OptionsIcon width='30px' height='30px'/>
                                    </button>
                                    */}
                                </div>
                                
                                {showSetupSummary ? (
                                    <div className='setup-summary-container'>
                                        {setupSummaries.length === 0 ? (
                                            <div className='setup-summary-empty'>No setups available.</div>
                                        ) : (
                                            <>
                                                {!hasAnySetupSummaryData && (
                                                    <div className='setup-summary-empty'>No checked shots available.</div>
                                                )}
                                                
                                            </>
                                        )}
                                        <div className='desktop-shotlist-container' hidden={!showShotList}>
                                            <ShotListsPanel
                                                sessionSetups={selectedSession ? selectedSession.SessionSetups || [] : []}
                                                activeSetupId={selectedSetup ? selectedSetup.Id : null}
                                                selectedShot={selectedShot}
                                                selectedShotSetupId={selectedShotSetupId}
                                                onShotSelected={handleShotSelected}
                                                handleShotChange={handleCurrentSessionRefresh}
                                                replacingShot={replacingShot}
                                                setReplacingShot={handleReplacingShot}
                                                showFavoritesOnly={showFavoritesOnly}
                                                onFavoritesToggle={handleFavoritesToggle}
                                                ShowAllSetups={showSetupSummary}
                                                justAvSt={true}
                                                ShowDetailedSummary={showDetailedSummary}
                                                onShotsChanged={refreshSetupsFromShots}
                                                
                                                
                                            />
                                           
                                        </div>
                                    </div>
                                    
                                    
                                ) : (
                                    <>
                                        {showShotList && (
                                            <div className='desktop-mini-widgets-container' onClick={() => handleSwapToWidgets()}>
                                                <MiniWidgets currentSetup={selectedSetup} currentShot={selectedShot} key={miniWidgetKey}/>
                                            </div>
                                        )}
                                        <div className='desktop-shotlist-container' hidden={!showShotList}>
                                            <ShotListsPanel
                                                sessionSetups={selectedSession ? selectedSession.SessionSetups || [] : []}
                                                activeSetupId={selectedSetup ? selectedSetup.Id : null}
                                                selectedShot={selectedShot}
                                                selectedShotSetupId={selectedShotSetupId}
                                                onShotSelected={handleShotSelected}
                                                handleShotChange={handleCurrentSessionRefresh}
                                                replacingShot={replacingShot}
                                                setReplacingShot={handleReplacingShot}
                                                showFavoritesOnly={showFavoritesOnly}
                                                onFavoritesToggle={handleFavoritesToggle}
                                                ShowAllSetups={showSetupSummary}
                                                ShowDetailedSummary={showDetailedSummary}
                                                onShotsChanged={refreshSetupsFromShots}
                                            />
                                           
                                        </div>
                                       
                                        <div className='desktop-widget-container' hidden={showShotList}>
                                            <Widgets currentSetup={selectedSetup} currentShot={selectedShot}/>
                                        </div>
                                    </>
                                )}

                            </div>
                        </div>
                     {/*   <div className='desktop-right-bar'>
                            <div style={{width:'100%', height:'25px'}}/>
                            <button className='desktop-right-bar-button' onClick={() => newSessionClick()}>
                                <Cross2Icon width={'30px'} height={'30px'}/>
                            </button>
                            <div style={{width:'100%', height:'10px'}}/>
                            <button className={`desktop-right-bar-button ${rightBarSelection === "SETTINGS" ? 'selected' : ''}`} onClick={() => handleRightBarButtonSelection("SETTINGS")}>
                                <div style={{width:'30px', height:'30px', transform:'rotate(270deg)'}}><FolderIcon width={'30px'} height={'30px'}/></div>
                                <span className='desktop-right-bar-button-text'>FILE</span>
                            </button>
                            <button className={`desktop-right-bar-button ${rightBarSelection === "OPTIONS" ? 'selected' : ''}`} onClick={() => handleRightBarButtonSelection("OPTIONS")}>
                                <div style={{width:'30px', height:'30px', marginLeft:'6px',marginBottom:'-2px',transform:'rotate(270deg)'}}><GearIcon width={'23px'} height={'23px'}/></div>
                                <span className='desktop-right-bar-button-text'>OPTIONS</span>
                            </button>
                            <div style={{width:'100%', height:'25px'}}/>
                            <button className={`desktop-right-bar-button ${rightBarSelection === "SETUPS" ? 'selected' : ''}`} onClick={() => handleRightBarButtonSelection("SETUPS")}>
                                <SetupsFlyoutButtonIcon width={'30px'} height={'30px'}/>
                                <span className='desktop-right-bar-button-text'>SETUPS</span>
                            </button>


                        </div>
                        */}
                        <div className="desktop-right-bar">
                            <div className="rb-spacer-lg" />

                            <button className="desktop-right-bar-button" onClick={newSessionClick}>
                                <div className="rb-icon"><Cross2Icon width="28px" height="28px" /></div>
                            </button>

                            <div className="rb-spacer-lg" />

                            <button
                                className={`desktop-right-bar-button ${rightBarSelection === "SETTINGS" ? "selected" : ""}`}
                                onClick={() => handleRightBarButtonSelection("SETTINGS")}
                            >
                                <div className="rb-icon rotate-270">
                                <FolderIcon width="28px" height="28px" />
                                </div>
                                <span className="desktop-right-bar-button-text">FILE</span>
                            </button>
                            <div className="rb-spacer-lg" />

                            <button
                                className={`desktop-right-bar-button ${rightBarSelection === "OPTIONS" ? "selected" : ""}`}
                                onClick={() => handleRightBarButtonSelection("OPTIONS")}
                            >
                                <div className="rb-icon">
                                <SettingsIcon width="28px" height="28px" />
                                </div>
                                <span className="desktop-right-bar-button-text">OPTIONS</span>
                            </button>

                            <div className="rb-spacer-lg" />

                            

                            <button
                                className={`desktop-right-bar-button ${rightBarSelection === "SETUPS" ? "selected" : ""}`}
                                onClick={() => handleRightBarButtonSelection("SETUPS")}
                            >
                                <div className="rb-icon">
                                <SetupsFlyoutButtonIcon width="28px" height="28px" />
                                </div>
                                <span className="desktop-right-bar-button-text">SETUPS</span>
                            </button>

                            <div className="rb-spacer-lg" />

                            {showCannonTab && (
                                <button
                                    className={`desktop-right-bar-button ${rightBarSelection === "CANNON" ? "selected" : ""}`}
                                    onClick={() => handleRightBarButtonSelection("CANNON")}
                                >
                                    <div className="rb-icon">
                                    <WidgetButtonFlowerIcon width="28px" height="28px" />
                                    </div>
                                    <span className="desktop-right-bar-button-text">COR/Cannon</span>
                                </button>
                            )}

                            </div>

                        <div className={`desktop-right-container ${rightPanelState ? 'open' : ''}`}>
                                {getRightPanelComponent()}
                        </div>
                        
                    </div>
                </div>
                <div className={`desktop-expanded-view-container ${maxamizePanelState ? 'open' : ''}`}>
                    <button className={`desktop-expanded-view-container-button ${
                        maxamizePanelState
                            ? 'open'
                            : maxamizePanelView === ''
                            ? 'empty'
                            : ''
                        }`} onClick={() => handleMaximizeMinMax()}>
                            {maxamizePanelState && (
                                <Cross2Icon width='40' height='40' className='desktop-expanded-view-chevron'/>
                            )}
                            <></>
                            {(!maxamizePanelState && maxamizePanelView != '' ) &&(
                                <></>
                            )}
                            <></>
                            {(maxamizePanelState && maxamizePanelView == '' ) &&(
                                <></>
                            )}
                        </button>
                    {getMaximizedComponent()}
                    <div id={'megaTraj'} style={{width:'100%', height:'100%'}}></div>
                </div>
                <div className='desktop-background'>

                </div>

            <Modal
                open={resetSessionOpen}>
                    <ResetSession resetSessionResult={newSession}/>
            </Modal>
            <Modal
                open={processedShotsOpen}>
                <ProcessTTShotsModal ProcessResult={handleProcessShotsClickResult}/>
            </Modal>
            <Modal open={setupSelectionOpen} onClose={handleCloseSetupSelection}>
                <Box className='setup-selection-modal'>
                    <SetupsList
                        currentSession={selectedSession}
                        setCurrentSetup={handleSetupSelectionFromModal}
                        Disabled={viewOnly}
                        Refresh={handleCurrentSessionRefresh}
                        confirmationMode
                        onConfirm={handleSetupSelectionConfirm}
                        onCancel={handleSetupSelectionCancel}
                        onShotsChanged={registerSetupsShotsChanged}
                    />
                </Box>
            </Modal>
            <Modal open={cannonCorConfirmation.open}>
                <Box className='cannon-cor-confirmation-modal'>
                    <div className='cannon-cor-confirmation-modal__title'>
                        Confirm COR Masses
                    </div>
                    <div className='cannon-cor-confirmation-modal__text'>
                        {cannonCorConfirmation.editable
                            ? 'Update the mass values for this setup, then accept to calculate COR.'
                            : 'Are these mass values correct for this setup?'}
                    </div>
                    <ResearchQuadMasses
                        visible={researchQuadConnected}
                        selectedSetup={confirmationSetup || activeCannonCorSetup}
                        plateSetup={plateCorSetup}
                        masses={researchQuadMasses}
                        onMassChange={setResearchQuadMasses}
                        disableInputs={!cannonCorConfirmation.editable}
                        persistOnChange={false}
                        forceNotCalculated
                        confirmMassesOnly
                        rows={(confirmationSetup || activeCannonCorSetup) ? [confirmationSetup || activeCannonCorSetup] : []}
                    />
                    {!!cannonCorConfirmation.error && (
                        <div className='cannon-cor-confirmation-modal__error'>
                            {cannonCorConfirmation.error}
                        </div>
                    )}
                    <div className='cannon-cor-confirmation-modal__actions'>
                        {!cannonCorConfirmation.editable ? (
                            <>
                                <button className='button' style={{ width: '100px' }} onClick={handleCannonCorConfirmationAccept}>Yes</button>
                                <button className='button' style={{ width: '100px' }} onClick={handleCannonCorConfirmationEdit}>No</button>
                            </>
                        ) : (
                            <button className='button' style={{ width: '120px' }} onClick={handleCannonCorConfirmationAccept}>Accept</button>
                        )}
                    </div>
                </Box>
            </Modal>
            <Modal open={postBallSeriesShotModalOpen}>
                <PostShotModal 
                    header={"Next Shot Ball Series"} 
                    modalState={setPostBallSeriesShotModalOpen}
                    buttonLabel={"Okay"}
                    usesTimer={true}
                    timeSeconds={5}>
                        <div style={{fontSize:'64px', fontWeight:'bolder'}}>{(selectedSession && selectedSession.BallSeries && selectedSession.BallSeries.length > 0 && selectedSetup && selectedSetup.Shots) ? selectedSession.BallSeries[selectedSetup.Shots.length] : ""}</div>
                </PostShotModal>
            </Modal>
            <Modal open={postBallSeriesShotEndedModalOpen}>
                <PostShotModal 
                    header={""} 
                    modalState={setPostBallSeriesShotEndedModalOpen}
                    buttonLabel={"Okay"}
                    usesTimer={false}
                    timeSeconds={5}>
                        <div style={{fontSize:'42px', fontWeight:'bolder'}}>{(selectedSetup) ? selectedSetup.Id +  " Finished" : "Series Finished"}</div>
                </PostShotModal>
            </Modal>
            <Modal open={impactOutsideModalOpen}>
                <PostShotModal
                    header={"Shot Outside Target"}
                    modalState={setImpactOutsideModalOpen}
                    buttonLabel={"Okay"}
                    usesTimer={false}
                    timeSeconds={5}>
                    <div style={{fontSize:'24px', fontWeight:'bolder'}}>
                        Shot #{impactOutsideModalData.shotNum ?? '--'} unchecked
                    </div>
                    <div style={{marginTop:'10px', fontSize:'16px'}}>
                        {impactOutsideModalData.setupName}
                    </div>
                    {impactOutsideModalData.targetName && (
                        <div style={{marginTop:'6px', fontSize:'16px'}}>
                            Target: {impactOutsideModalData.targetName}
                        </div>
                    )}
                    <div style={{marginTop:'10px', fontSize:'18px'}}>
                        Offset: {impactOutsideModalData.dxMm.toFixed(2)} mm, {impactOutsideModalData.dyMm.toFixed(2)} mm
                    </div>
                </PostShotModal>
            </Modal>
            <Modal open={setupToleranceModalOpen}>
                <PostShotModal
                    header={"Setup Tolerances Met"}
                    modalState={setSetupToleranceModalOpen}
                    buttonLabel={"Okay"}
                    usesTimer={false}
                    timeSeconds={5}>
                    <div style={{fontSize:'24px', fontWeight:'bolder'}}>
                        You may continue the test.
                    </div>
                    <div style={{marginTop:'10px', fontSize:'16px'}}>
                        {setupToleranceModalData.setupName}
                    </div>
                    <div style={{marginTop:'6px', fontSize:'16px'}}>
                        Shot #{setupToleranceModalData.shotNum ?? '--'}
                    </div>
                </PostShotModal>
            </Modal>
            </div>
        );
        }
        
        const MobileRender = () =>{
        return (
            <div className='container'>
                    {headerRender()}
                    <div className='innerContainer'>
                        <Drawer
                            style={{overflow:"hidden"}}
                            anchor='right'
                            open={drawerOpen}
                            onClose={toggleDrawer(false)}
                        >
                            {drawerContent}
                        </Drawer>
                        <Drawer
                            style={{overflow:"hidden"}}
                            anchor='left'
                            open={settingsOpen}
                            onClose={toggleSettings(false)}
                        >
                            {settingsContent}
                        </Drawer>
                        {renderSwitch()}
                    </div>
                    <div className='footerContainer'>
                    <BottomNavigation
                        style={
                            { 
                                width:"100%",
                                background: getComputedStyle(document.documentElement).getPropertyValue('--background-primary')
                            }
                        }
                        showLabels
                        value={currentView}
                        onChange={(event, newValue) => {
                            changeView(newValue);
                        }}
                        >
                        <BottomNavigationAction value="MINIWIDGETS" style={
                            {
                                background : currentView === "MINIWIDGETS" ? getComputedStyle(document.documentElement).getPropertyValue('--selected-highlight') : "transparent",
                                color : getComputedStyle(document.documentElement).getPropertyValue('--foreground-primary')
                            }
                            } label="MINIWIDGETS" icon={<WidgetButtonIcon width='40px' height='40px'/>} />
                        <BottomNavigationAction value="WIDGETS" style={
                            {
                                background : currentView === "WIDGETS" ? getComputedStyle(document.documentElement).getPropertyValue('--selected-highlight') : "transparent",
                                color : getComputedStyle(document.documentElement).getPropertyValue('--foreground-primary')
                            }
                            } label="WIDGETS" icon={<WidgetButtonIcon width='40px' height='40px'/>} />
                        <BottomNavigationAction value="SHOTLIST" style={
                            {
                                background : currentView === "SHOTLIST" ? getComputedStyle(document.documentElement).getPropertyValue('--selected-highlight') : "transparent",
                                color : getComputedStyle(document.documentElement).getPropertyValue('--foreground-primary')
                            }
                            } label="SHOTLIST" icon={<ShotListButtonIcon width='40px' height='40px'/>} />
                        <BottomNavigationAction value="THREEDTRAJ" style={
                            {
                                background : currentView === "THREEDTRAJ" ? getComputedStyle(document.documentElement).getPropertyValue('--selected-highlight') : "transparent",
                                color : getComputedStyle(document.documentElement).getPropertyValue('--foreground-primary')
                            }
                            } label="3D" icon={<ThreeDTrajIcon width='40px' height='40px'/>} />
                        <BottomNavigationAction value="TOPDOWN" style={
                            {
                                background : currentView === "TOPDOWN" ? getComputedStyle(document.documentElement).getPropertyValue('--selected-highlight') : "transparent",
                                color : getComputedStyle(document.documentElement).getPropertyValue('--foreground-primary')
                            }
                            } label="TOP DOWN" icon={<TopDownIcon width='40px' height='40px'/>} />
                    </BottomNavigation>
                    </div>
                </div>
        );
    };

    return (
        <>
        {!isMobile && (
            <>
            {DesktopRender()}
            </>
        )}
        <></>
        {isMobile && (
            <>
            {MobileRender()}
            </>
        )}
        </>
        
    );
}

export default Session;
